import React, { useState, useEffect, useCallback, useRef } from 'react';
import { GET, POST, PUT, DELETE, FORM_POST } from '../services/api';
import TenureSelect from '../components/TenureSelect';
import DateRangeFilter, { NO_CUSTOM_DATES } from '../components/DateRangeFilter';
import { useMasterChapter, MasterChapterLockNote } from '../context/MasterChapter';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
    Search, Users as UsersIcon, Plus, Eye, EyeOff, Pencil, Trash,
    Lock, LockOpen, RefreshCw, X, Check, Filter, ChevronDown,
    MapPin, Phone, Mail, Briefcase, User, Building, Settings,
    AlertTriangle, Info, Image as ImageIcon, Shield, FileText, Loader, Download, ArrowLeft, UserPlus
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Member activity record: what this member passed, what they received, and with
// whom. Backed by GET /member-activity/:userId, which shares its date window
// with the chapter report — the same filter produces the same numbers.
// ---------------------------------------------------------------------------

const DATE_RANGES = [
    'This Week', 'Last Week', 'Last 3 Months', 'Last 4 Months',
    'Last 6 Months', 'Last 9 Months', 'Last 12 Months', 'All Time',
];

const inr = (value) =>
    `₹${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const activityDate = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    return Number.isNaN(d.getTime())
        ? '—'
        : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

/**
 * A figure in the activity summary. Selecting one opens its detail below —
 * only the chosen list is on screen, rather than every list at once.
 */
const StatTile = ({ label, value, sub, tone = 'slate', count, selected, onSelect }) => {
    const tones = {
        emerald: 'text-emerald-700 bg-emerald-50 border-emerald-100',
        blue: 'text-blue-700 bg-blue-50 border-blue-100',
        violet: 'text-violet-700 bg-violet-50 border-violet-100',
        amber: 'text-amber-700 bg-amber-50 border-amber-100',
        rose: 'text-rose-700 bg-rose-50 border-rose-100',
        slate: 'text-slate-700 bg-slate-50 border-slate-200',
    };
    const rings = {
        emerald: 'ring-emerald-400', blue: 'ring-blue-400', violet: 'ring-violet-400',
        amber: 'ring-amber-400', rose: 'ring-rose-400', slate: 'ring-slate-400',
    };
    const empty = count === 0;

    return (
        <button
            type="button"
            onClick={onSelect}
            disabled={empty}
            aria-pressed={selected}
            className={`rounded-xl border p-4 text-left transition-shadow ${tones[tone]}
                ${empty ? 'cursor-default opacity-60' : 'hover:shadow-md'}
                ${selected ? `ring-2 ring-offset-1 ${rings[tone]}` : ''}`}
        >
            <div className="text-xs font-semibold uppercase tracking-wide opacity-70">{label}</div>
            <div className="tabular mt-1 text-xl font-extrabold leading-tight">{value}</div>
            <div className="tabular mt-0.5 text-xs font-medium opacity-70">
                {sub || (empty ? 'Nothing recorded' : selected ? 'Showing below' : 'View details')}
            </div>
        </button>
    );
};

const MemberChip = ({ member }) => (
    <div className="flex items-center gap-2 min-w-0">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[10px] font-bold text-slate-500">
            {(member?.name || '?').split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase()}
        </div>
        <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-slate-800">{member?.name || '—'}</div>
            {member?.business_name && (
                <div className="truncate text-xs text-slate-400">{member.business_name}</div>
            )}
        </div>
    </div>
);

const ICON_TONES = {
    emerald: 'text-emerald-500',
    blue: 'text-blue-500',
    violet: 'text-violet-500',
    rose: 'text-rose-500',
    slate: 'text-slate-400',
};

/** One activity list. `columns` describe the row beyond the counterparty. */
const ActivityTable = ({ title, icon: Icon, tone, rows, counterpartyLabel, columns, dateLabel, onClose }) => (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3.5">
            <h4 className="flex items-center gap-2 text-sm font-bold text-slate-800">
                <Icon className={`h-4 w-4 ${ICON_TONES[tone]}`} /> {title}
                <span className="tabular rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-bold text-slate-600">
                    {rows.length}
                </span>
            </h4>
            {onClose && (
                <button
                    onClick={onClose}
                    className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                    aria-label={`Close ${title}`}
                >
                    <X className="h-4 w-4" />
                </button>
            )}
        </div>

        {rows.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-slate-400">
                Nothing recorded in this period.
            </p>
        ) : (
            <div className="max-h-80 overflow-y-auto abn-scroll">
                <table className="w-full text-left">
                    <thead className="sticky top-0 bg-slate-50 text-xs font-bold uppercase tracking-wide text-slate-500">
                        <tr>
                            <th className="px-5 py-2.5 font-bold">{dateLabel || 'Date'}</th>
                            <th className="px-5 py-2.5 font-bold">{counterpartyLabel}</th>
                            {columns.map((c) => (
                                <th key={c.key} className={`px-5 py-2.5 font-bold ${c.align || ''}`}>
                                    {c.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                        {rows.map((row) => (
                            <tr key={row.id} className="hover:bg-slate-50/70">
                                <td className="tabular whitespace-nowrap px-5 py-3 text-sm text-slate-500">
                                    {activityDate(row.date)}
                                </td>
                                <td className="px-5 py-3"><MemberChip member={row.member} /></td>
                                {columns.map((c) => (
                                    <td key={c.key} className={`px-5 py-3 text-sm text-slate-600 ${c.align || ''}`}>
                                        {c.render(row)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )}
    </div>
);

const AMOUNT_COL = { key: 'amount', label: 'Amount', align: 'text-right', render: (r) => <span className="tabular font-bold text-slate-800">{inr(r.amount)}</span> };
const REMARKS_COL = { key: 'remarks', label: 'Remarks', render: (r) => r.remarks || '—' };
const REFERRAL_COL = { key: 'ref', label: 'Referral', render: (r) => r.referral_name || '—' };
const REFERRAL_PHONE_COL = { key: 'phone', label: 'Phone', render: (r) => <span className="tabular">{r.referral_phone || '—'}</span> };

/**
 * The six activity figures. One definition drives both the summary tile and the
 * table it opens, so a label or column can't drift between them.
 */
const SECTIONS = [
    {
        key: 'business_given', label: 'Business passed', tone: 'emerald', icon: Briefcase,
        counterpartyLabel: 'Passed to',
        value: (s) => inr(s.business_given.amount),
        sub: (s) => `${s.business_given.count} slip${s.business_given.count === 1 ? '' : 's'}`,
        columns: [AMOUNT_COL, REMARKS_COL],
    },
    {
        key: 'business_received', label: 'Business received', tone: 'blue', icon: Briefcase,
        counterpartyLabel: 'Received from',
        value: (s) => inr(s.business_received.amount),
        sub: (s) => `${s.business_received.count} slip${s.business_received.count === 1 ? '' : 's'}`,
        columns: [AMOUNT_COL, REMARKS_COL],
    },
    {
        key: 'one_to_one', label: 'One-to-ones', tone: 'violet', icon: UsersIcon,
        counterpartyLabel: 'Met with',
        value: (s) => s.one_to_one.count,
        sub: (s) => `${s.one_to_one.hosted} hosted`,
        columns: [
            {
                key: 'direction', label: 'Role',
                render: (r) => (
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${r.direction === 'hosted' ? 'bg-violet-50 text-violet-700' : 'bg-slate-100 text-slate-600'}`}>
                        {r.direction === 'hosted' ? 'Hosted' : 'Attended'}
                    </span>
                ),
            },
            { key: 'location', label: 'Location', render: (r) => r.location || '—' },
        ],
    },
    {
        key: 'references_given', label: 'References passed', tone: 'slate', icon: FileText,
        counterpartyLabel: 'Passed to',
        value: (s) => s.references_given.count,
        columns: [REFERRAL_COL, REFERRAL_PHONE_COL],
    },
    {
        key: 'references_received', label: 'References received', tone: 'slate', icon: FileText,
        counterpartyLabel: 'Received from',
        value: (s) => s.references_received.count,
        columns: [REFERRAL_COL, REFERRAL_PHONE_COL],
    },
    {
        key: 'members_joined', label: 'Members joined', tone: 'rose', icon: UserPlus,
        counterpartyLabel: 'Member', dateLabel: 'Joined',
        value: (s) => s.members_joined.total,
        sub: (s) => `${s.members_joined.count} in this period`,
        columns: [
            { key: 'chapter', label: 'Chapter', render: (r) => r.chapter_name || '—' },
            {
                key: 'window', label: 'In this period',
                render: (r) => (
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${r.in_window ? 'bg-rose-50 text-rose-700' : 'bg-slate-100 text-slate-500'}`}>
                        {r.in_window ? 'Yes' : 'Earlier'}
                    </span>
                ),
            },
        ],
    },
];

const MemberActivity = ({ userId, chapterId, initialSection, initialDateRange, initialTenureId, initialStartDate, initialEndDate }) => {
    const [dateRange, setDateRange] = useState(
        DATE_RANGES.includes(initialDateRange) ? initialDateRange : 'Last 3 Months'
    );
    const [tenureId, setTenureId] = useState(initialTenureId || '');
    const [customDates, setCustomDates] = useState(
        initialStartDate && initialEndDate ? { startDate: initialStartDate, endDate: initialEndDate } : NO_CUSTOM_DATES
    );
    const [record, setRecord] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [openSection, setOpenSection] = useState(
        SECTIONS.some((x) => x.key === initialSection) ? initialSection : null
    );

    useEffect(() => {
        if (!userId) return undefined;
        let active = true;

        const load = async () => {
            setLoading(true);
            setError('');
            try {
                const params = new URLSearchParams();
                // Server precedence: tenure, then custom dates, then dateRange — send only one.
                if (tenureId) params.set('tenureId', tenureId);
                else if (customDates.startDate) {
                    params.set('startDate', customDates.startDate);
                    params.set('endDate', customDates.endDate);
                } else params.set('dateRange', dateRange);

                const res = await GET(`/member-activity/${userId}?${params.toString()}`);
                if (active) setRecord(res.data);
            } catch (err) {
                if (active) {
                    setError(err.message || 'Could not load this member’s activity.');
                    setRecord(null);
                }
            } finally {
                if (active) setLoading(false);
            }
        };

        load();
        return () => { active = false; };
    }, [userId, dateRange, tenureId, customDates]);

    const s = record?.summary;

    return (
        <div>
            {/* Filters */}
            <div className="relative z-20 grid grid-cols-1 gap-4 rounded-2xl border border-slate-200 bg-white p-5 sm:grid-cols-2 lg:grid-cols-3">
                <DateRangeFilter
                    ranges={DATE_RANGES}
                    range={dateRange}
                    customDates={customDates}
                    disabled={!!tenureId}
                    note={tenureId ? 'Overridden by the selected tenure.' : ''}
                    onChange={(r, c) => { setDateRange(r); setCustomDates(c); }}
                />

                <TenureSelect
                    chapterId={chapterId}
                    value={tenureId}
                    onChange={setTenureId}
                    className="relative"
                />

                <div className="flex items-end">
                    <button
                        onClick={() => { setDateRange('Last 3 Months'); setTenureId(''); setCustomDates(NO_CUSTOM_DATES); }}
                        className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:border-blue-200 hover:text-blue-600"
                    >
                        Reset filters
                    </button>
                </div>
            </div>

            {loading && (
                <div className="flex items-center justify-center gap-3 py-16 text-slate-400">
                    <Loader className="h-5 w-5 animate-spin" /> Loading activity…
                </div>
            )}

            {!loading && error && (
                <div className="flex items-start gap-3 rounded-2xl border border-red-100 bg-red-50 p-5 text-sm text-red-700">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {error}
                </div>
            )}

            {!loading && !error && record && (
                <>
                    <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
                        {SECTIONS.map((section) => (
                            <StatTile
                                key={section.key}
                                tone={section.tone}
                                label={section.label}
                                value={section.value(s)}
                                sub={section.sub?.(s)}
                                count={record[section.key].length}
                                selected={openSection === section.key}
                                onSelect={() =>
                                    setOpenSection(openSection === section.key ? null : section.key)
                                }
                            />
                        ))}
                        <StatTile
                            tone="amber" label="Attendance"
                            value={`${s.attendance.present} / ${s.attendance.total}`}
                            sub={`${s.attendance.absent} absent`}
                            count={0}
                        />
                    </div>

                    {openSection ? (
                        (() => {
                            const section = SECTIONS.find((x) => x.key === openSection);
                            return (
                                <ActivityTable
                                    title={section.label}
                                    icon={section.icon}
                                    tone={section.tone}
                                    rows={record[section.key]}
                                    counterpartyLabel={section.counterpartyLabel}
                                    dateLabel={section.dateLabel}
                                    columns={section.columns}
                                    onClose={() => setOpenSection(null)}
                                />
                            );
                        })()
                    ) : (
                        <p className="rounded-2xl border border-dashed border-slate-200 px-5 py-8 text-center text-sm text-slate-400">
                            Select a figure above to see the records behind it.
                        </p>
                    )}
                </>
            )}
        </div>
    );
};

/**
 * One list row / detail record, built from a user plus its business profile.
 * The list, the detail page and the edit form all read this same shape, so a
 * field renamed here changes everywhere at once.
 */
const mapUserRow = (user, cityMap, chapterMap) => {

            const business = user.business || {};
            const cityId = business.city_id?._id || business.city_id || business.city?._id || business.city || '';
            const chapterId = business.chapter_id?._id || business.chapter_id || business.chapter?._id || business.chapter || '';
            const cityName = business.city?.name || business.city_name || cityMap.get(cityId) || '';
            const chapterName = business.chapter?.name || business.chapter_name || chapterMap.get(chapterId) || '';
            const servicesOffered = Array.isArray(business.services_offered)
                ? business.services_offered
                : Array.isArray(business.services)
                    ? business.services.map((s) => s.service_title)
                    : typeof business.services_offered === 'string'
                        ? business.services_offered.split(/\r?\n|,/).map((s) => s.trim()).filter(Boolean)
                        : [];

            const userProfilePicture = user.profile_picture || '';
            const businessProfilePicture = business.profile_picture || '';
            const resolvedProfilePicture =
                userProfilePicture && userProfilePicture !== 'default-profile.png'
                    ? userProfilePicture
                    : businessProfilePicture || userProfilePicture;

            return {
                ...user,
                id: user._id,
                name: `${user.first_name || ''} ${user.last_name || ''}`.trim(),
                businessProfileStatus: business._id ? 'Completed' : 'Incompleted',
                businessName: business.business_name || 'Business Not Registered',
                city: cityName,
                chapter: chapterName,
                cityId,
                chapterId,
                isActive: user.isActive !== undefined ? user.isActive : true,
                accountStatus: user.account_status || '',
                business: business,
                personalPhoneNumber: business.personal_phone_number || '',
                officePhoneNumber: business.office_phone_number || '',
                email2: business.email || '',
                businessWebsite: business.web_url || '',
                businessTagline: business.tagline || '',
                descriptionOfBusiness: business.description || '',
                businessServices: servicesOffered.join('\n'),
                joiningDate: business.joining_date ? new Date(business.joining_date).toISOString().split('T')[0] : null,
                reference: business.reference || '',
                profilePicture: resolvedProfilePicture,
                dob: user.dob ? new Date(user.dob).toISOString().split('T')[0] : '',
                role: user.role || 'member',
                fullName: business.full_name || '',
                // Address fields
                hometownAddress: business.hometown_address || '',
                hometownCity: business.hometown_city || '',
                hometownDistrict: business.hometown_district || '',
                hometownPincode: business.hometown_pincode || '',
                residenceAddress: business.residence_address || '',
                residenceCity: business.residence_city || '',
                residenceDistrict: business.residence_district || '',
                residencePincode: business.residence_pincode || '',
                residenceLatitude: business.residence_latitude || '',
                residenceLongitude: business.residence_longitude || '',
                officeAddress: business.office_address || '',
                officeCity: business.office_city || '',
                officeDistrict: business.office_district || '',
                officePincode: business.office_pincode || '',
                officeLatitude: business.office_latitude || '',
                officeLongitude: business.office_longitude || '',
                // Image fields
                businessLogo: business.business_logo || '',
                businessCardFront: business.business_card_front || '',
                businessCardBack: business.business_card_back || '',
                portfolioImages: business.portfolio_images || []
            };
};

// Link Component
const Link = (props) => <a href={props.to} {...props}>{props.children}</a>;

// --- Global Styles ---
const CustomStyles = () => (
    <style>{`
    .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
    .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 8px; }
    .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 8px; }
    .custom-scrollbar::-webkit-scrollbar-thumb:hover { background-color: #94a3b8; }
    .animate-fade-in-up { animation: fade-in-up 0.4s ease-out forwards; }
    @keyframes fade-in-up { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
  `}</style>
);

// --- Helper: View Field Component ---
/**
 * A read-only field on the member profile. Shown as a compact label/value pair
 * rather than a boxed input — these are facts to read, not controls to fill,
 * and the boxes cost ~86px each down a twenty-field column.
 */
const InfoField = ({ label, value, fullWidth = false }) => {
    const isServices = label === 'Business Services' && value && value !== '-';
    return (
        <div className={`border-b border-slate-100 py-2 last:border-0 ${fullWidth ? 'col-span-2' : ''}`}>
            <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{label}</div>
            {isServices ? (
                <ul className="mt-0.5 space-y-0.5">
                    {value.split('\n').map((service, index) => (
                        <li key={index} className="flex gap-1.5 text-sm font-semibold text-slate-700">
                            <span className="text-slate-300">&bull;</span>
                            <span>{service.trim()}</span>
                        </li>
                    ))}
                </ul>
            ) : (
                <div className="mt-0.5 break-words text-sm font-semibold text-slate-700">
                    {value === 'N/A' ? '-' : value || '-'}
                </div>
            )}
        </div>
    );
};

// --- Helper: Multi-Select Dropdown (From Dashboard) ---
const MultiSelectDropdown = ({ options, selectedValues, onSelectChange, placeholder, label, disabled = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);
  
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setIsOpen(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
  
    const handleOptionClick = (value) => {
      let newSelectedValues;
      if (selectedValues.includes(value)) {
        newSelectedValues = selectedValues.filter(item => item !== value);
      } else {
        newSelectedValues = [...selectedValues, value];
      }
      onSelectChange(newSelectedValues);
    };
  
    // Filter options based on search term
    const filteredOptions = options.filter(option => {
        const val = option.name || option.label || option;
        return String(val).toLowerCase().includes(searchTerm.toLowerCase());
    });
  
    return (
      <div className="relative group" ref={dropdownRef}>
        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 ml-1">{label}</label>
        <div
          className={`w-full p-2.5 bg-white border rounded-xl min-h-[46px] flex items-center justify-between transition-all duration-200 
          ${disabled ? 'opacity-60 cursor-not-allowed border-slate-200' : isOpen ? 'cursor-pointer border-blue-500 ring-2 ring-blue-100 shadow-sm' : 'cursor-pointer border-slate-200 hover:border-blue-300'}`}
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          <div className="flex-grow overflow-hidden">
            {selectedValues.length === 0 ? (
              <span className="text-slate-400 text-sm">{placeholder}</span>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {selectedValues.slice(0, 2).map((value) => (
                  <span key={value} className="flex items-center bg-blue-50 text-blue-700 px-2 py-0.5 rounded-md text-xs font-medium border border-blue-100">
                    {value}
                    {!disabled && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleOptionClick(value); }}
                      className="ml-1 text-blue-400 hover:text-blue-600 focus:outline-none"
                    >
                      <X className="w-3 h-3" />
                    </button>
                    )}
                  </span>
                ))}
                {selectedValues.length > 2 && (
                  <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded-md text-xs font-medium">
                    +{selectedValues.length - 2} more
                  </span>
                )}
              </div>
            )}
          </div>
          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
        </div>
  
        {isOpen && !disabled && (
          <div className="absolute top-full left-0 mt-2 w-full bg-white border border-slate-100 rounded-xl shadow-xl z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-100 origin-top">
            <div className="p-2 border-b border-slate-50 bg-slate-50/50">
              <input
                type="text"
                placeholder="Search..."
                className="w-full p-2 text-sm bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
            </div>
            <div className="max-h-56 overflow-y-auto p-1 custom-scrollbar">
              {filteredOptions.length === 0 && (
                <div className="p-3 text-sm text-slate-400 text-center">No results found</div>
              )}
              {filteredOptions.map((option, idx) => {
                const val = option.name || option.label || option;
                return (
                  <div
                    key={option._id || idx}
                    className={`flex items-center px-3 py-2.5 rounded-lg text-sm cursor-pointer transition-colors ${selectedValues.includes(val) ? 'bg-blue-50 text-blue-700' : 'text-slate-700 hover:bg-slate-50'}`}
                    onClick={() => handleOptionClick(val)}
                  >
                    <div className={`w-4 h-4 border rounded mr-3 flex items-center justify-center flex-shrink-0 transition-colors ${selectedValues.includes(val) ? 'bg-blue-600 border-blue-600' : 'border-slate-300 bg-white'}`}>
                      {selectedValues.includes(val) && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <span className="truncate">{val}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
};

const Users = () => {
    // --- State Management ---
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    
    // Stats
    const [userStats, setUserStats] = useState({ totalUsers: 0, enabledUsers: 0, disabledUsers: 0 });
    
    // Tab State (status filter)
    const [statusTab, setStatusTab] = useState('total'); // 'total', 'active', 'inactive'

    const [searchTerm, setSearchTerm] = useState('');
    
    // Multi-select Filters (by name). The super-admin master chapter filter locks both.
    const master = useMasterChapter();
    const masterRef = useRef(master);
    masterRef.current = master;
    const [filterCities, setFilterCities] = useState(() => (master.chapterId ? [master.cityName] : []));
    const [filterChapters, setFilterChapters] = useState(() => (master.chapterId ? [master.chapterName] : []));

    useEffect(() => {
        // Keep the previous array when nothing changed so the list isn't refetched for nothing
        const lockTo = (value) => (prev) => (prev.length === value.length && prev.every((v, i) => v === value[i]) ? prev : value);
        setFilterCities(lockTo(master.chapterId ? [master.cityName] : []));
        setFilterChapters(lockTo(master.chapterId ? [master.chapterName] : []));
    }, [master.chapterId, master.cityName, master.chapterName]);

    const { id: routeUserId } = useParams();
    const navigate = useNavigate();
    const routePath = useLocation().pathname;

    // /admin/users -> list, /admin/users/new -> create,
    // /admin/users/:id -> detail, /admin/users/:id/edit -> edit
    const pageMode = routePath.endsWith('/new')
        ? 'new'
        : routeUserId
            ? (routePath.endsWith('/edit') ? 'edit' : 'detail')
            : 'list';

    // The chapter report deep-links into a member's activity record with one
    // section already open and its own filter carried over.
    const [searchParams] = useSearchParams();
    const linkedTab = searchParams.get('tab');
    const linkedSection = searchParams.get('section');
    const linkedDateRange = searchParams.get('dateRange');
    const linkedTenureId = searchParams.get('tenureId');
    const linkedStartDate = searchParams.get('startDate');
    const linkedEndDate = searchParams.get('endDate');

    const [viewUser, setViewUser] = useState(null);
    const [viewTab, setViewTab] = useState(linkedTab === 'activity' ? 'activity' : 'profile');
    const [routeUserError, setRouteUserError] = useState('');
    const [viewUserReferenceName, setViewUserReferenceName] = useState('');

    const [users, setUsers] = useState([]);
    const [allCities, setAllCities] = useState([]);
    const [allChapters, setAllChapters] = useState([]);
    const [filteredChaptersForForm, setFilteredChaptersForForm] = useState([]);

    const [loading, setLoading] = useState(true);
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const [error, setError] = useState(null);

    // Password state for editing (optional password change)
    const [editPasswordData, setEditPasswordData] = useState({ newPassword: '', confirmPassword: '' });

    const [toastMessage, setToastMessage] = useState(null);
    const [confirmationDialog, setConfirmationDialog] = useState(null);
    
    // Disable Remark Modal State
    const [showDisableModal, setShowDisableModal] = useState(false);
    const [userToDisable, setUserToDisable] = useState(null);
    const [disableRemark, setDisableRemark] = useState('');
    const [isProcessingDisable, setIsProcessingDisable] = useState(false);

    const [togglingUserId, setTogglingUserId] = useState(null);

    // Reference search states
    const [referenceSearch, setReferenceSearch] = useState('');
    const [referenceResults, setReferenceResults] = useState([]);
    const [referenceLoading, setReferenceLoading] = useState(false);
    const [referenceDebounceTimer, setReferenceDebounceTimer] = useState(null);
    const [showReferenceDropdown, setShowReferenceDropdown] = useState(false);

    // --- Form States ---
    const [showAddEditModal, setShowAddEditModal] = useState(false);
    const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
    const [activeTab, setActiveTab] = useState('personal');
    const [editingUserId, setEditingUserId] = useState(null);
    const [editingBusinessData, setEditingBusinessData] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);

    const [formData, setFormData] = useState({
        firstName: '', lastName: '', fullName: '', email: '', phone: '', dateOfBirth: '', account_status: 'active', role: 'member',
        city_id: '', chapter_id: '', joiningDate: '', reference: '', business_name: '',
        password: '', confirmPassword: '', // For ADD mode
        profile_picture: null, business_logo: null, tagline: '', description: '', business_services: [],
        business_card_front: null, business_card_back: null, portfolio_images: [],
        hometown_address: '', hometown_city: '', hometown_district: '', hometown_pincode: '',
        residence_address: '', residence_city: '', residence_district: '', residence_pincode: '',
        residence_latitude: '', residence_longitude: '',
        office_address: '', office_city: '', office_district: '', office_pincode: '',
        office_latitude: '', office_longitude: '',
        personal_phone_number: '', office_phone_number: '', business_email: '', web_url: ''
    });

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({}); // Track which fields have errors
    const [serviceInput, setServiceInput] = useState(''); // Temporary service input
    const [isPlacesReady, setIsPlacesReady] = useState(false);
    const [addressSuggestions, setAddressSuggestions] = useState({ hometown: [], residence: [], office: [] });
    const [showAddressSuggestions, setShowAddressSuggestions] = useState({ hometown: false, residence: false, office: false });

    const gujaratBoundsRef = useRef(null);
    const autocompleteServiceRef = useRef(null);
    const placesServiceRef = useRef(null);
    const addressDebounceRef = useRef({});
    const addressSessionTokensRef = useRef({});


    // --- Service Handlers ---
    const handleAddService = () => {
        const trimmedService = serviceInput.trim();
        if (trimmedService !== '' && !formData.business_services.includes(trimmedService)) {
            setFormData({ ...formData, business_services: [...formData.business_services, trimmedService] });
            setServiceInput('');
        }
    };

    const handleRemoveService = (indexToRemove) => {
        const newServices = formData.business_services.filter((_, index) => index !== indexToRemove);
        setFormData({ ...formData, business_services: newServices });
    };

    const handleServiceKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddService();
        }
    };

    // --- Helpers ---
    const showToast = useCallback((message, severity = 'info') => {
        setToastMessage({ message, severity });
    }, []);

    useEffect(() => {
        if (toastMessage) {
            const timer = setTimeout(() => setToastMessage(null), 3000);
            return () => clearTimeout(timer);
        }
    }, [toastMessage]);

    useEffect(() => {
        const apiKey = process.env.REACT_APP_GOOGLE_PLACES_API_KEY;
        if (!apiKey) return;

        if (window.google && window.google.maps && window.google.maps.places) {
            setIsPlacesReady(true);
            return;
        }

        const existingScript = document.querySelector('script[data-places-api]');
        if (existingScript) {
            existingScript.addEventListener('load', () => setIsPlacesReady(true));
            return;
        }

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.setAttribute('data-places-api', 'true');
        script.onload = () => setIsPlacesReady(true);
        script.onerror = () => console.error('Failed to load Google Places API');
        document.head.appendChild(script);
    }, []);

    const getAddressComponent = (components, type) => {
        const match = components?.find((c) => c.types.includes(type));
        return match ? match.long_name : '';
    };

        const applyPlaceToForm = (place, kind, options = {}) => {
        const includeLatLng = options.includeLatLng !== false;
        const components = place.address_components || [];
        const city = getAddressComponent(components, 'locality') || getAddressComponent(components, 'administrative_area_level_2');
            const district =
                getAddressComponent(components, 'administrative_area_level_2') ||
                getAddressComponent(components, 'administrative_area_level_3') ||
                getAddressComponent(components, 'sublocality_level_1') ||
                getAddressComponent(components, 'sublocality');
        const pincode = getAddressComponent(components, 'postal_code');

        const lat = place.geometry?.location?.lat();
        const lng = place.geometry?.location?.lng();

        setFormData((prev) => {
            const next = {
                ...prev,
                [`${kind}_address`]: place.formatted_address || place.name || '',
                [`${kind}_city`]: city || prev[`${kind}_city`],
                [`${kind}_district`]: district || prev[`${kind}_district`],
                [`${kind}_pincode`]: pincode || prev[`${kind}_pincode`]
            };

            if (includeLatLng) {
                next[`${kind}_latitude`] = typeof lat === 'number' ? lat : prev[`${kind}_latitude`];
                next[`${kind}_longitude`] = typeof lng === 'number' ? lng : prev[`${kind}_longitude`];
            }

            return next;
        });
    };

    const getAddressSessionToken = (kind) => {
        if (!addressSessionTokensRef.current[kind]) {
            addressSessionTokensRef.current[kind] = new window.google.maps.places.AutocompleteSessionToken();
        }
        return addressSessionTokensRef.current[kind];
    };

    const resetAddressSessionToken = (kind) => {
        addressSessionTokensRef.current[kind] = new window.google.maps.places.AutocompleteSessionToken();
    };

    const fetchAddressPredictions = (kind, value) => {
        if (!isPlacesReady || !autocompleteServiceRef.current) return;
        if (!value) {
            setAddressSuggestions((prev) => ({ ...prev, [kind]: [] }));
            return;
        }

        const request = {
            input: value,
            componentRestrictions: { country: 'in' },
            sessionToken: getAddressSessionToken(kind)
        };

        if (gujaratBoundsRef.current) {
            request.bounds = gujaratBoundsRef.current;
        }

        autocompleteServiceRef.current.getPlacePredictions(request, (predictions, status) => {
            if (status !== window.google.maps.places.PlacesServiceStatus.OK || !predictions) {
                setAddressSuggestions((prev) => ({ ...prev, [kind]: [] }));
                return;
            }

            setAddressSuggestions((prev) => ({ ...prev, [kind]: predictions }));
            setShowAddressSuggestions((prev) => ({ ...prev, [kind]: true }));
        });
    };

    const debounceAddressPredictions = (kind, value) => {
        if (addressDebounceRef.current[kind]) {
            clearTimeout(addressDebounceRef.current[kind]);
        }

        addressDebounceRef.current[kind] = setTimeout(() => {
            fetchAddressPredictions(kind, value);
        }, 500);
    };

    const handleAddressInputChange = (kind, value) => {
        setFormData((prev) => {
            const next = { ...prev, [`${kind}_address`]: value };
            if (kind === 'residence' || kind === 'office') {
                next[`${kind}_latitude`] = '';
                next[`${kind}_longitude`] = '';
            }
            return next;
        });

        debounceAddressPredictions(kind, value);
    };

    const handleAddressFocus = (kind) => {
        setShowAddressSuggestions((prev) => ({ ...prev, [kind]: true }));
        const currentValue = formData[`${kind}_address`];
        if (currentValue) {
            fetchAddressPredictions(kind, currentValue);
        }
    };

    const handleAddressBlur = (kind) => {
        setTimeout(() => {
            setShowAddressSuggestions((prev) => ({ ...prev, [kind]: false }));
        }, 150);
    };

    const handleAddressSuggestionSelect = (kind, prediction) => {
        setShowAddressSuggestions((prev) => ({ ...prev, [kind]: false }));
        setAddressSuggestions((prev) => ({ ...prev, [kind]: [] }));

        setFormData((prev) => ({
            ...prev,
            [`${kind}_address`]: prediction.description || prev[`${kind}_address`]
        }));

        if (!placesServiceRef.current) return;

        placesServiceRef.current.getDetails({
            placeId: prediction.place_id,
            fields: ['address_components', 'geometry', 'formatted_address', 'name'],
            sessionToken: getAddressSessionToken(kind)
        }, (place, status) => {
            if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) return;
            applyPlaceToForm(place, kind, { includeLatLng: kind !== 'hometown' });
            resetAddressSessionToken(kind);
        });
    };

    useEffect(() => {
        if (!isPlacesReady) return;

        if (!gujaratBoundsRef.current) {
            gujaratBoundsRef.current = new window.google.maps.LatLngBounds(
                new window.google.maps.LatLng(21.1702, 70.8022),
                new window.google.maps.LatLng(23.0225, 72.8311)
            );
        }

        if (!autocompleteServiceRef.current) {
            autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
        }

        if (!placesServiceRef.current) {
            placesServiceRef.current = new window.google.maps.places.PlacesService(document.createElement('div'));
        }
    }, [isPlacesReady]);

    useEffect(() => {
        if (showAddEditModal && activeTab === 'address') return;

        setShowAddressSuggestions({ hometown: false, residence: false, office: false });
        setAddressSuggestions({ hometown: [], residence: [], office: [] });

        Object.values(addressDebounceRef.current).forEach((timer) => clearTimeout(timer));
        addressDebounceRef.current = {};
    }, [activeTab, showAddEditModal]);

    const showConfirmation = useCallback((header, message, acceptCallback, rejectCallback) => {
        setConfirmationDialog({ header, message, onConfirm: acceptCallback, onCancel: rejectCallback || (() => { }), });
    }, []);

    const isValidImage = (src) => {
        if (!src) return false;
        if (src.startsWith('data:image/')) return true;
        if (src.startsWith('http')) return true;
        return false;
    };

    const fetchReferenceName = async (userId) => {
        try {
            const response = await GET(`/reference-name/${userId}`);
            return response.data ? response.data.full_name : userId;
        } catch (error) {
            console.error('Error fetching reference name:', error);
            return userId;
        }
    };

    useEffect(() => {
        const fetchViewUserReferenceName = async () => {
            if (viewUser && viewUser.reference) {
                const name = await fetchReferenceName(viewUser.reference);
                setViewUserReferenceName(name);
            } else {
                setViewUserReferenceName('');
            }
        };
        fetchViewUserReferenceName();
    }, [viewUser]);

    useEffect(() => { setCurrentPage(1); }, [searchTerm, filterCities, filterChapters, statusTab]);

    // --- API Calls ---
    const fetchAllData = useCallback(async (isBackground = false) => {
        setError(null);
        if (!isBackground) setLoading(true);

        try {
            const queryParams = new URLSearchParams({ page: currentPage, limit: itemsPerPage });
            if (searchTerm) queryParams.append('search', searchTerm);
            
            // Handle Multi-Select Arrays by joining with comma
            if (filterCities.length > 0) queryParams.append('city', filterCities.join(','));
            if (filterChapters.length > 0) queryParams.append('chapter', filterChapters.join(','));
            
            // --- UPDATED STATUS FILTER LOGIC ---
            // Mapping tabs to isActive boolean for the backend
            // --- STATUS FILTER (BACKEND COMPATIBLE) ---
            if (statusTab === 'active') queryParams.append('status', 'enabled');
            if (statusTab === 'inactive') queryParams.append('status', 'disabled');

            const [usersData, citiesData, chaptersData] = await Promise.all([
                GET(`/users?${queryParams.toString()}`),
                GET(`/users/cities`),
                GET(`/users/chapters`)
            ]);

            const actualUsersArray = usersData.data && Array.isArray(usersData.data) ? usersData.data : [];
            if (usersData.pagination && typeof usersData.pagination.totalRecords === 'number') {
                setTotalRecords(usersData.pagination.totalRecords);
            } else {
                setTotalRecords(actualUsersArray.length);
            }
            
            // Update Stats based on backend response
            if (usersData.stats) {
                setUserStats({
                    totalUsers: usersData.stats.totalUsers || 0,
                    enabledUsers: usersData.stats.enabledUsers || usersData.stats.activeUsers || 0,
                    disabledUsers: usersData.stats.disabledUsers || usersData.stats.inactiveUsers || 0
                });
            }

            const citiesList = citiesData.data || citiesData || [];
            const chaptersList = chaptersData.data || chaptersData || [];
            const cityMap = new Map(citiesList.map((c) => [c._id, c.name]));
            const chapterMap = new Map(chaptersList.map((c) => [c._id, c.name]));

            const combinedUsers = actualUsersArray.map((user) => mapUserRow(user, cityMap, chapterMap));

            setUsers(combinedUsers);
            setAllCities(citiesList);
            setAllChapters(chaptersList);
        } catch (err) {
            console.error("Error fetching data:", err);
            setError(err.message);
            showToast(`Failed to load data: ${err.message}`, 'error');
        } finally {
            setLoading(false);
        }
    }, [currentPage, itemsPerPage, searchTerm, filterCities, filterChapters, statusTab, showToast]);

    useEffect(() => {
        const timer = setTimeout(() => { fetchAllData(); }, 500);
        return () => clearTimeout(timer);
    }, [fetchAllData]);

    const totalPages = Math.ceil(totalRecords / itemsPerPage);

    const handleDownloadPdf = async () => {
        setDownloadingPdf(true);
        try {
            const params = new URLSearchParams();
            if (searchTerm) params.append('search', searchTerm);
            if (filterCities.length > 0) params.append('city', filterCities.join(','));
            if (filterChapters.length > 0) params.append('chapter', filterChapters.join(','));

            const token = localStorage.getItem('authToken');
            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/users/download-pdf?${params.toString()}`,
                {
                    method: 'GET',
                    headers: { Authorization: token ? `Bearer ${token}` : '' },
                }
            );

            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.message || 'Failed to download PDF');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `members_${new Date().toISOString().split('T')[0]}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
            showToast('PDF downloaded successfully', 'success');
        } catch (error) {
            console.error('Error downloading PDF:', error);
            showToast(error.message || 'Failed to download PDF', 'error');
        } finally {
            setDownloadingPdf(false);
        }
    };

    // --- Reference Search ---
    const searchReferences = useCallback(async (searchText) => {
        if (!searchText.trim()) {
            setReferenceResults([]);
            setReferenceLoading(false);
            return;
        }
        setReferenceLoading(true);
        try {
            const response = await GET(`/reference-search?search=${encodeURIComponent(searchText)}`);
            setReferenceResults(response.data || []);
        } catch (error) {
            console.error('Error searching references:', error);
            setReferenceResults([]);
        } finally {
            setReferenceLoading(false);
        }
    }, []);

    const handleReferenceInputChange = useCallback((e) => {
        const value = e.target.value;
        setReferenceSearch(value);
        setFormData(prev => ({ ...prev, reference: value }));
        if (referenceDebounceTimer) clearTimeout(referenceDebounceTimer);
        const newTimer = setTimeout(() => {
            searchReferences(value);
            setShowReferenceDropdown(true);
        }, 300);
        setReferenceDebounceTimer(newTimer);
    }, [referenceDebounceTimer, searchReferences]);

    const handleReferenceSelect = useCallback((member) => {
        setReferenceSearch(member.full_name);
        setFormData(prev => ({ ...prev, reference: member.user_id }));
        setReferenceResults([]);
        setShowReferenceDropdown(false);
    }, []);

    // --- Toggle User Status (Enable/Disable) ---
    const handleToggleClick = (user) => {
        if (user.isActive) {
            // Logic: Active -> Inactive (Needs Remark)
            setUserToDisable(user);
            setDisableRemark('');
            setShowDisableModal(true);
        } else {
            // Logic: Inactive -> Active (Direct Action)
            showConfirmation(
                'Enable User',
                `Are you sure you want to enable ${user.name}?`,
                () => performToggle(user, true, '')
            );
        }
    };

    const confirmDisableUser = async () => {
        if (!disableRemark.trim()) {
            showToast('Please enter a remark for disabling this user.', 'error');
            return;
        }
        setIsProcessingDisable(true);
        await performToggle(userToDisable, false, disableRemark);
        setIsProcessingDisable(false);
        setShowDisableModal(false);
        setUserToDisable(null);
    };

    const performToggle = async (user, nextIsActive, remark) => {
        try {
            setTogglingUserId(user.id);
            const payload = { isActive: nextIsActive };
            if (remark) payload.remark = remark; // Send remark to API

            await PUT(`/users/${user.id}`, payload);

            setUsers((prevUsers) =>
                prevUsers.map((u) =>
                    u.id === user.id ? { ...u, isActive: nextIsActive } : u
                )
            );
            
            // Update stats locally
            setUserStats(prev => ({
                ...prev,
                enabledUsers: nextIsActive ? prev.enabledUsers + 1 : prev.enabledUsers - 1,
                disabledUsers: nextIsActive ? prev.disabledUsers - 1 : prev.disabledUsers + 1
            }));

            showToast(`User ${nextIsActive ? 'enabled' : 'disabled'} successfully!`, 'success');
        } catch (err) {
            showToast(err.message || 'Failed to update user status', 'error');
        } finally {
            setTogglingUserId(null);
        }
    };

    // --- Form Logic ---
    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const fetchChaptersForCity = useCallback(async (selectedCityId) => {
        if (!selectedCityId || typeof selectedCityId !== 'string') { setFilteredChaptersForForm([]); return; }
        try {
            const data = await GET(`/users/chapters/city/${selectedCityId}`);
            setFilteredChaptersForForm(data);
        } catch (error) {
            showToast(error.message || 'Failed to load chapters', 'error');
            setFilteredChaptersForForm([]);
        }
    }, [showToast]);

    useEffect(() => {
        const fetchAndSetChapters = async () => {
            if (formData.city_id) {
                await fetchChaptersForCity(formData.city_id);
                if (formData.chapter_id) setFormData(prev => ({ ...prev, chapter_id: formData.chapter_id }));
            } else { setFilteredChaptersForForm([]); }
        };
        fetchAndSetChapters();
    }, [formData.city_id, fetchChaptersForCity]);

    const handleSubmit = async () => {
        // --- COMPREHENSIVE VALIDATION ---
        const errors = {};

        // Email and website validation regex
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const websiteRegex = /^(https?:\/\/|www\.)[^\s]+$/i;

        // Personal Tab Validation
        if (!formData.firstName.trim()) errors.firstName = 'First Name is required';
        if (!formData.lastName.trim()) errors.lastName = 'Last Name is required';
        if (!formData.email.trim()) {
            errors.email = 'Email is required';
        } else if (!emailRegex.test(formData.email)) {
            errors.email = 'Please enter a valid email address';
        }
        if (formData.phone.trim() && !/^\d{10}$/.test(formData.phone)) {
            errors.phone = 'Phone number must be exactly 10 digits';
        }

        // Business Tab Validation
        if (!formData.city_id) errors.city_id = 'City is required';
        if (!formData.chapter_id) errors.chapter_id = 'Chapter is required';
        if (!formData.business_name.trim()) errors.business_name = 'Business Name is required';
        if (!formData.fullName.trim()) errors.fullName = 'Full Name is required';
        if (!formData.joiningDate.trim()) errors.joiningDate = 'Joining Date is required';
        if (formData.personal_phone_number.trim() && !/^\d{10}$/.test(formData.personal_phone_number)) {
            errors.personal_phone_number = 'Phone number must be exactly 10 digits';
        }
        
        if (formData.office_phone_number.trim() && !/^\d{10}$/.test(formData.office_phone_number)) {
            errors.office_phone_number = 'Phone number must be exactly 10 digits';
        }
        
        if (formData.business_email.trim() && !emailRegex.test(formData.business_email)) {
            errors.business_email = 'Please enter a valid email address';
        }

        // Website validation (optional but must be valid if provided)
        if (formData.web_url.trim() && !websiteRegex.test(formData.web_url.trim())) {
            errors.web_url = 'Website URL must start with http://, https://, or www.';
        }

        // Address Tab Validation (only format check if provided)
        if (formData.hometown_pincode.trim() && !/^\d{6}$/.test(formData.hometown_pincode)) errors.hometown_pincode = 'Pincode must be exactly 6 digits';
        if (formData.residence_pincode.trim() && !/^\d{6}$/.test(formData.residence_pincode)) errors.residence_pincode = 'Pincode must be exactly 6 digits';
        if (formData.office_pincode.trim() && !/^\d{6}$/.test(formData.office_pincode)) errors.office_pincode = 'Pincode must be exactly 6 digits';

        // Password Validation
        if (modalMode === 'add') {
            if (!formData.password) errors.password = 'Password is required';
            if (!formData.confirmPassword) errors.confirmPassword = 'Confirm Password is required';
            if (formData.password && formData.password.length < 6) errors.passwordLength = 'Password must be at least 6 characters';
            if (formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword) errors.passwordMismatch = 'Passwords do not match';
        } else {
            // Edit mode: password is optional, but if provided, must match
            if (formData.password || formData.confirmPassword) {
                if (formData.password !== formData.confirmPassword) errors.passwordMismatch = 'Passwords do not match';
                if (formData.password && formData.password.length < 6) errors.passwordLength = 'Password must be at least 6 characters';
            }
        }

        // Check if there are any errors
        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);

            // Auto-navigate to first tab with errors
            if (errors.firstName || errors.lastName || errors.email || errors.phone || errors.password || errors.confirmPassword || errors.passwordLength || errors.passwordMismatch) {
                setActiveTab('personal');
            } else if (errors.city_id || errors.chapter_id || errors.business_name || errors.fullName || errors.joiningDate || errors.personal_phone_number || errors.office_phone_number || errors.business_email || errors.web_url) {
                setActiveTab('business');
            } else if (errors.hometown_pincode || errors.residence_pincode || errors.office_pincode) {
                setActiveTab('address');
            }

            // Scroll to the first visible error message in the active tab
            setTimeout(() => {
                const errorEl = document.querySelector('.field-error-marker');
                if (errorEl) {
                    errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 50);

            showToast('Please fill all required fields', 'error');
            return;
        }

        setFieldErrors({});
        const action = modalMode === 'add' ? 'Create' : 'Update';
        showConfirmation(
            `${action} Member`,
            `Are you sure you want to ${modalMode === 'add' ? 'create this new member' : 'update this member'}?`,
            async () => {
                setModalLoading(true);
                try {
                    if (modalMode === 'add') {
                        // ADD MODE: Create both user and business
                        let newUserId;
                        
                        // Check if there's a pending user ID from previous failed attempt
                        const pendingUserId = localStorage.getItem('pendingUserCreation');
                        
                        if (pendingUserId) {
                            // Use existing user ID (retry scenario)
                            newUserId = pendingUserId;
                        } else {
                            // Create new user
                            const userFormData = new FormData();
                            userFormData.append('first_name', formData.firstName);
                            userFormData.append('last_name', formData.lastName);
                            userFormData.append('email', formData.email);
                            userFormData.append('phone', formData.phone);
                            userFormData.append('dob', formData.dateOfBirth);
                            userFormData.append('password', formData.password);
                            userFormData.append('role', 'member');
                            if (formData.profile_picture) userFormData.append('profile_picture', formData.profile_picture);

                            const userResponse = await FORM_POST(`/users`, userFormData);
                            newUserId = userResponse.user._id;
                            
                            // Store user ID in localStorage for retry scenario
                            localStorage.setItem('pendingUserCreation', newUserId);
                        }

                        const businessFormData = new FormData();
                        businessFormData.append('user_id', newUserId);
                        businessFormData.append('city_id', formData.city_id);
                        businessFormData.append('chapter_id', formData.chapter_id);
                        businessFormData.append('business_name', formData.business_name);
                        businessFormData.append('full_name', formData.fullName);
                        businessFormData.append('joining_date', formData.joiningDate);
                        businessFormData.append('reference', formData.reference);
                        businessFormData.append('tagline', formData.tagline);
                        businessFormData.append('description', formData.description);
                        businessFormData.append('personal_phone_number', formData.personal_phone_number);
                        businessFormData.append('office_phone_number', formData.office_phone_number);
                        businessFormData.append('email', formData.business_email);
                        businessFormData.append('web_url', formData.web_url);
                        
                        const services = formData.business_services.map(s => s.trim()).filter(Boolean);
                        services.forEach(service => businessFormData.append('services_offered', service));

                        businessFormData.append('hometown_address', formData.hometown_address);
                        businessFormData.append('hometown_city', formData.hometown_city);
                        businessFormData.append('hometown_district', formData.hometown_district);
                        businessFormData.append('hometown_pincode', formData.hometown_pincode);
                        businessFormData.append('residence_address', formData.residence_address);
                        businessFormData.append('residence_city', formData.residence_city);
                        businessFormData.append('residence_district', formData.residence_district);
                        businessFormData.append('residence_pincode', formData.residence_pincode);
                        businessFormData.append('residence_latitude', formData.residence_latitude || '');
                        businessFormData.append('residence_longitude', formData.residence_longitude || '');
                        businessFormData.append('office_address', formData.office_address);
                        businessFormData.append('office_city', formData.office_city);
                        businessFormData.append('office_district', formData.office_district);
                        businessFormData.append('office_pincode', formData.office_pincode);
                        businessFormData.append('office_latitude', formData.office_latitude || '');
                        businessFormData.append('office_longitude', formData.office_longitude || '');

                        if (formData.business_logo) businessFormData.append('business_logo', formData.business_logo);
                        if (formData.business_card_front) businessFormData.append('business_card_front', formData.business_card_front);
                        if (formData.business_card_back) businessFormData.append('business_card_back', formData.business_card_back);
                        if (formData.portfolio_images && formData.portfolio_images.length > 0) {
                            formData.portfolio_images.forEach(file => businessFormData.append('portfolio_images', file));
                        }

                        try {
                            await FORM_POST(`/businesses`, businessFormData);
                            
                            // Business created successfully - clear pending user ID
                            localStorage.removeItem('pendingUserCreation');
                        } catch (businessErr) {
                            console.error('Business creation error:', businessErr);
                            
                            // Keep the user ID in localStorage for retry
                            // User will see error message and can fix business data and retry
                            setModalLoading(false);
                            showToast(`Business creation failed: ${businessErr.message}. Please fix the business details and try again.`, 'error');
                            return;
                        }
                        showToast('Member created successfully!', 'success');
                    } else {
                        // EDIT MODE: Update user and business
                        const userFormData = new FormData();
                        userFormData.append('first_name', formData.firstName);
                        userFormData.append('last_name', formData.lastName);
                        userFormData.append('email', formData.email);
                        userFormData.append('phone', formData.phone);
                        userFormData.append('dob', formData.dateOfBirth);
                        userFormData.append('account_status', formData.account_status);
                        userFormData.append('role', formData.role);

                        if (editPasswordData.newPassword) {
                            userFormData.append('password', editPasswordData.newPassword);
                        }

                        await FORM_POST(`/users/${editingUserId}`, userFormData, 'PUT');

                        const businessFormData = new FormData();
                        businessFormData.append('full_name', formData.fullName);
                        businessFormData.append('city_id', formData.city_id);
                        businessFormData.append('chapter_id', formData.chapter_id);
                        businessFormData.append('joining_date', formData.joiningDate);
                        businessFormData.append('reference', formData.reference);
                        businessFormData.append('business_name', formData.business_name);
                        businessFormData.append('tagline', formData.tagline);
                        businessFormData.append('description', formData.description);
                        const servicesValues = formData.business_services.map(s => s.trim()).filter(Boolean);
                        servicesValues.forEach((service) => {
                            businessFormData.append('services_offered', service);
                        });
                        businessFormData.append('office_address', formData.office_address);
                        businessFormData.append('office_city', formData.office_city);
                        businessFormData.append('office_district', formData.office_district);
                        businessFormData.append('office_pincode', formData.office_pincode);
                        businessFormData.append('office_latitude', formData.office_latitude || '');
                        businessFormData.append('office_longitude', formData.office_longitude || '');
                        businessFormData.append('residence_address', formData.residence_address);
                        businessFormData.append('residence_city', formData.residence_city);
                        businessFormData.append('residence_district', formData.residence_district);
                        businessFormData.append('residence_pincode', formData.residence_pincode);
                        businessFormData.append('residence_latitude', formData.residence_latitude || '');
                        businessFormData.append('residence_longitude', formData.residence_longitude || '');
                        businessFormData.append('hometown_address', formData.hometown_address);
                        businessFormData.append('hometown_city', formData.hometown_city);
                        businessFormData.append('hometown_district', formData.hometown_district);
                        businessFormData.append('hometown_pincode', formData.hometown_pincode);
                        businessFormData.append('personal_phone_number', formData.personal_phone_number);
                        businessFormData.append('office_phone_number', formData.office_phone_number);
                        businessFormData.append('email', formData.business_email);
                        businessFormData.append('web_url', formData.web_url);

                        if (formData.profile_picture instanceof File) {
                            businessFormData.append('profile_picture', formData.profile_picture);
                        }
                        if (formData.business_logo instanceof File) {
                            businessFormData.append('business_logo', formData.business_logo);
                        }
                        if (formData.business_card_front instanceof File) {
                            businessFormData.append('business_card_front', formData.business_card_front);
                        }
                        if (formData.business_card_back instanceof File) {
                            businessFormData.append('business_card_back', formData.business_card_back);
                        }
                        if (formData.portfolio_images && formData.portfolio_images.length > 0) {
                            formData.portfolio_images.forEach(file => {
                                if (file instanceof File) {
                                    businessFormData.append('portfolio_images', file);
                                }
                            });
                        }

                        if (editingBusinessData && editingBusinessData._id) {
                            await FORM_POST(`/businesses/${editingBusinessData._id}`, businessFormData, 'PUT');
                        } else {
                            businessFormData.append('user_id', editingUserId);
                            await FORM_POST(`/businesses`, businessFormData);
                        }

                        showToast('Member updated successfully!', 'success');
                    }

                    await fetchAllData();
                    localStorage.removeItem('pendingUserCreation'); // Clear any pending state
                    closeForm();
                    setEditPasswordData({ newPassword: '', confirmPassword: '' });
                    setEditingUserId(null);
                } catch (err) {
                    console.error('Form submission error:', err);
                    showToast(`Error: ${err.message}`, 'error');
                } finally {
                    setModalLoading(false);
                }
            }
        );
    };

    const handleDeleteUser = useCallback((userId) => {
        showConfirmation('Delete User', 'Are you sure?', async () => {
            try {
                await DELETE(`/users/${userId}`);
                showToast('User deleted successfully!', 'success');
                fetchAllData();
            } catch (err) { showToast(err.message, 'error'); }
        });
    }, [fetchAllData, showToast, showConfirmation]);

    const handleEditUser = useCallback(async (user) => {
        setModalMode('edit');
        setEditPasswordData({ newPassword: '', confirmPassword: '' });

        let businessData = user.business || null;
        let userDetails = user;
        try {
            const fetchedData = await GET(`/businesses/user/${user._id}`);
            businessData = Array.isArray(fetchedData) ? fetchedData[0] : fetchedData;
            setEditingBusinessData(businessData);
        } catch (error) {
            console.error('Error fetching business data:', error);
            showToast(error.message || 'Failed to load business', 'error');
            return;
        }

        try {
            const fetchedUser = await GET(`/users/${user._id || user.id}`);
            if (fetchedUser) userDetails = fetchedUser;
        } catch (error) {
            console.error('Error fetching user details:', error);
            showToast(error.message || 'Failed to load user', 'error');
            return;
        }

        setEditingUserId(user.id);

        let fetchedServices = [];
        if (Array.isArray(businessData?.services_offered)) {
            fetchedServices = businessData.services_offered.filter(s => s.trim() !== '');
        } else if (Array.isArray(businessData?.services)) {
            fetchedServices = businessData.services.map((s) => s.service_title).filter(s => s.trim() !== '');
        } else if (typeof businessData?.services_offered === 'string') {
            fetchedServices = businessData.services_offered.split(/[,\n]/).map(s => s.trim()).filter(Boolean);
        } else if (businessData?._id) {
            try {
                const sData = await GET(`/business-services/business/${businessData._id}`);
                if (sData && sData.length) fetchedServices = sData.map(s => s.service_title).filter(s => s.trim() !== '');
            } catch (e) { }
        }

        setFormData({
            firstName: userDetails.first_name || user.first_name || '',
            lastName: userDetails.last_name || user.last_name || '',
            fullName: businessData?.full_name || '',
            email: userDetails.email || user.email || '',
            phone: userDetails.phone || user.phone || '',
            dateOfBirth: (userDetails.dob || user.dob) ? new Date(userDetails.dob || user.dob).toISOString().split('T')[0] : '',
            account_status: userDetails.account_status || user.account_status || 'active',
            role: 'member',
            city_id: businessData?.city_id?._id || businessData?.city_id || businessData?.city?._id || businessData?.city || '',
            chapter_id: businessData?.chapter_id?._id || businessData?.chapter_id || businessData?.chapter?._id || businessData?.chapter || '',
            joiningDate: businessData?.joining_date ? new Date(businessData.joining_date).toISOString().split('T')[0] : '',
            reference: businessData?.reference || '',
            business_name: businessData?.business_name || '',
            tagline: businessData?.tagline || '',
            description: businessData?.description || '',
            business_services: fetchedServices,
            business_email: businessData?.email || '',
            personal_phone_number: businessData?.personal_phone_number || '',
            office_phone_number: businessData?.office_phone_number || '',
            web_url: businessData?.web_url || '',
            hometown_address: businessData?.hometown_address || '',
            hometown_city: businessData?.hometown_city || '',
            hometown_district: businessData?.hometown_district || '',
            hometown_pincode: businessData?.hometown_pincode || '',
            residence_address: businessData?.residence_address || '',
            residence_city: businessData?.residence_city || '',
            residence_district: businessData?.residence_district || '',
            residence_pincode: businessData?.residence_pincode || '',
            residence_latitude: businessData?.residence_latitude ?? '',
            residence_longitude: businessData?.residence_longitude ?? '',
            office_address: businessData?.office_address || '',
            office_city: businessData?.office_city || '',
            office_district: businessData?.office_district || '',
            office_pincode: businessData?.office_pincode || '',
            office_latitude: businessData?.office_latitude ?? '',
            office_longitude: businessData?.office_longitude ?? '',
            profile_picture: null,
            business_logo: null,
            business_card_front: null,
            business_card_back: null,
            portfolio_images: [],
            password: '',
            confirmPassword: ''
        });

        if (businessData?.reference) {
            const name = await fetchReferenceName(businessData.reference);
            setReferenceSearch(name);
        } else {
            setReferenceSearch('');
        }

        setServiceInput('');
        setFieldErrors({});
        setActiveTab('personal');
        setShowAddEditModal(true);
    }, [showToast]);

    const handleAddMember = useCallback(() => {
        // Clear any pending user creation from previous failed attempts
        localStorage.removeItem('pendingUserCreation');
        
        setModalMode('add');
        setEditingUserId(null);
        setEditingBusinessData(null);
        setFormData({
            firstName: '', lastName: '', fullName: '', email: '', phone: '', dateOfBirth: '', account_status: 'active', role: 'member',
            // Pre-filled from the super-admin master chapter filter; still editable
            city_id: masterRef.current.cityId || '', chapter_id: masterRef.current.chapterId || '', joiningDate: '', reference: '', business_name: '',
            password: '', confirmPassword: '',
            profile_picture: null, business_logo: null, tagline: '', description: '', business_services: [],
            business_card_front: null, business_card_back: null, portfolio_images: [],
            hometown_address: '', hometown_city: '', hometown_district: '', hometown_pincode: '',
            residence_address: '', residence_city: '', residence_district: '', residence_pincode: '',
            residence_latitude: '', residence_longitude: '',
            office_address: '', office_city: '', office_district: '', office_pincode: '',
            office_latitude: '', office_longitude: '',
            personal_phone_number: '', office_phone_number: '', business_email: '', web_url: ''
        });
        setFieldErrors({});
        setServiceInput('');
        setReferenceSearch('');
        setActiveTab('personal');
        setShowAddEditModal(true);
    }, []);

    // A detail/edit URL can be opened cold (refresh, bookmark, new tab), so fall
    // back to fetching just that member and mapping it the same way the list does.
    useEffect(() => {
        if (!routeUserId) { setViewUser(null); setRouteUserError(''); return undefined; }

        const fromList = users.find((u) => u.id === routeUserId);
        if (fromList) { setViewUser(fromList); setRouteUserError(''); return undefined; }

        let active = true;
        (async () => {
            try {
                const [user, businessRes, citiesData, chaptersData] = await Promise.all([
                    GET(`/users/${routeUserId}`),
                    GET(`/businesses/user/${routeUserId}`).catch(() => null),
                    GET('/users/cities'),
                    GET('/users/chapters'),
                ]);
                if (!active) return;

                const businessList = businessRes?.data || businessRes || [];
                const business = Array.isArray(businessList) ? businessList[0] : businessList;
                const cityMap = new Map((citiesData.data || citiesData || []).map((c) => [c._id, c.name]));
                const chapterMap = new Map((chaptersData.data || chaptersData || []).map((c) => [c._id, c.name]));

                setViewUser(mapUserRow({ ...(user.data || user), business: business || {} }, cityMap, chapterMap));
                setRouteUserError('');
            } catch (err) {
                if (active) setRouteUserError(err.message || 'This member could not be loaded.');
            }
        })();
        return () => { active = false; };
    }, [routeUserId, users]);

    /** Leaving the add/edit form always returns to the list. */
    const closeForm = useCallback(() => {
        setShowAddEditModal(false);
        navigate('/admin/users');
    }, [navigate]);

    const handleCloseModal = useCallback(() => {
        // Clear pending user creation if user closes modal without completing
        const pendingUserId = localStorage.getItem('pendingUserCreation');
        if (pendingUserId && modalMode === 'add') {
            // Ask for confirmation before closing if there's a pending user
            if (window.confirm('You have unsaved changes. If you close now, you may need to delete the created user manually. Are you sure?')) {
                localStorage.removeItem('pendingUserCreation');
                closeForm();
            }
        } else {
            closeForm();
        }
    }, [modalMode, closeForm]);

    // The add/edit form keeps its own loaded/unloaded flag; the route decides when
    // to load it, so a direct link to /new or /:id/edit arrives ready to type in.
    const formRouteRef = useRef(null);
    useEffect(() => {
        const routeKey = pageMode === 'new' ? 'new' : pageMode === 'edit' ? routeUserId : null;
        if (!routeKey) { formRouteRef.current = null; return; }
        if (formRouteRef.current === routeKey) return;

        if (pageMode === 'new') {
            formRouteRef.current = routeKey;
            handleAddMember();
        } else if (viewUser) {
            formRouteRef.current = routeKey;
            handleEditUser(viewUser);
        }
    }, [pageMode, routeUserId, viewUser, handleAddMember, handleEditUser]);

    // --- Reusable Input Class ---
    const inputClass = "w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-medium text-slate-700 transition-all text-sm";
    const labelClass = "block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5 ml-1";
    const errorMessageClass = "text-xs text-red-500 font-bold mt-1 ml-1 field-error-marker";

    // --- Edit Form Content by Tab ---
    const renderAddEditContent = () => {
        switch (activeTab) {
            case 'personal':
                return (
                    <div className="space-y-6 animate-fade-in-up">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <label className={labelClass}>First Name *</label>
                                <input type="text" value={formData.firstName} onChange={(e) => handleInputChange('firstName', e.target.value)} className={inputClass} />
                                {fieldErrors.firstName && <p className={errorMessageClass}>{fieldErrors.firstName}</p>}
                            </div>
                            <div>
                                <label className={labelClass}>Last Name *</label>
                                <input type="text" value={formData.lastName} onChange={(e) => handleInputChange('lastName', e.target.value)} className={inputClass} />
                                {fieldErrors.lastName && <p className={errorMessageClass}>{fieldErrors.lastName}</p>}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <label className={labelClass}>Email *</label>
                                <input type="email" value={formData.email} onChange={(e) => handleInputChange('email', e.target.value)} className={inputClass} />
                                {fieldErrors.email && <p className={errorMessageClass}>{fieldErrors.email}</p>}
                            </div>
                            <div>
                                <label className={labelClass}>Date of Birth</label>
                                <input type="date" value={formData.dateOfBirth} onChange={(e) => handleInputChange('dateOfBirth', e.target.value)} className={inputClass} />
                                {fieldErrors.dateOfBirth && <p className={errorMessageClass}>{fieldErrors.dateOfBirth}</p>}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <label className={labelClass}>Phone Number</label>
                                <input 
                                    type="tel" 
                                    value={formData.phone} 
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                                        handleInputChange('phone', value);
                                    }} 
                                    className={inputClass}
                                    placeholder="10 digits only"
                                    maxLength={10}
                                />
                                {fieldErrors.phone && <p className={errorMessageClass}>{fieldErrors.phone}</p>}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <label className={labelClass}>User Status</label>
                                <select value={formData.account_status} onChange={(e) => handleInputChange('account_status', e.target.value)} className={inputClass}>
                                    <option value="active">Active</option>
                                    <option value="pending">Pending</option>
                                </select>
                            </div>
                            <div>
                                <label className={labelClass}>Role</label>
                                <input type="text" value="Member" disabled className={`${inputClass} opacity-60 cursor-not-allowed`} />
                            </div>
                        </div>

                        {/* PASSWORD SECTION - Different for ADD vs EDIT */}
                        {modalMode === 'add' ? (
                            <div className="mt-8 pt-6 border-t border-slate-100">
                                <h5 className="font-bold text-slate-700 mb-5 text-sm flex items-center gap-2"><Lock className="w-4 h-4 text-red-500" /> Security (Required for New Member)</h5>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <div>
                                        <label className={labelClass}>Password *</label>
                                        <div className="relative">
                                            <input 
                                                type={showPassword ? "text" : "password"} 
                                                value={formData.password} 
                                                onChange={(e) => handleInputChange('password', e.target.value)} 
                                                className={inputClass} 
                                                placeholder="Min 6 characters"
                                            />
                                            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-3 text-slate-400 hover:text-slate-600">
                                                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </button>
                                        </div>
                                        {fieldErrors.password && <p className={errorMessageClass}>{fieldErrors.password}</p>}
                                        {fieldErrors.passwordLength && <p className={errorMessageClass}>{fieldErrors.passwordLength}</p>}
                                    </div>
                                    <div>
                                        <label className={labelClass}>Confirm Password *</label>
                                        <div className="relative">
                                            <input 
                                                type={showConfirmPassword ? "text" : "password"} 
                                                value={formData.confirmPassword} 
                                                onChange={(e) => handleInputChange('confirmPassword', e.target.value)} 
                                                className={inputClass} 
                                                placeholder="Confirm password"
                                            />
                                            <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-3 text-slate-400 hover:text-slate-600">
                                                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </button>
                                        </div>
                                        {fieldErrors.confirmPassword && <p className={errorMessageClass}>{fieldErrors.confirmPassword}</p>}
                                        {fieldErrors.passwordMismatch && <p className={errorMessageClass}>{fieldErrors.passwordMismatch}</p>}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="mt-8 pt-6 border-t border-slate-100">
                                <h5 className="font-bold text-slate-700 mb-5 text-sm flex items-center gap-2"><Lock className="w-4 h-4 text-blue-500" /> Change Password (Optional)</h5>
                                <p className="text-xs text-slate-400 mb-4">Leave blank to keep the current password unchanged.</p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <div>
                                        <label className={labelClass}>New Password</label>
                                        <div className="relative">
                                            <input 
                                                type={showPassword ? "text" : "password"} 
                                                value={editPasswordData.newPassword} 
                                                onChange={(e) => setEditPasswordData({ ...editPasswordData, newPassword: e.target.value })} 
                                                className={inputClass} 
                                                placeholder="Leave blank to keep current"
                                            />
                                            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-3 text-slate-400 hover:text-slate-600">
                                                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </button>
                                        </div>
                                    </div>
                                    <div>
                                        <label className={labelClass}>Confirm Password</label>
                                        <div className="relative">
                                            <input 
                                                type={showConfirmPassword ? "text" : "password"} 
                                                value={editPasswordData.confirmPassword} 
                                                onChange={(e) => setEditPasswordData({ ...editPasswordData, confirmPassword: e.target.value })} 
                                                className={inputClass} 
                                                placeholder="Confirm password"
                                            />
                                            <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-3 text-slate-400 hover:text-slate-600">
                                                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                );
             case 'business':
                return (
                    <div className="space-y-6 animate-fade-in-up">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <label className={labelClass}>City *</label>
                                <select value={formData.city_id || ''} onChange={(e) => handleInputChange('city_id', e.target.value)} className={inputClass}>
                                    <option value="">Select City</option>
                                    {allCities.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                                </select>
                                {fieldErrors.city_id && <p className={errorMessageClass}>{fieldErrors.city_id}</p>}
                            </div>
                            <div>
                                <label className={labelClass}>Chapter *</label>
                                <select value={formData.chapter_id} onChange={(e) => handleInputChange('chapter_id', e.target.value)} className={inputClass}>
                                    <option value="">Select Chapter</option>
                                    {filteredChaptersForForm.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                                </select>
                                {fieldErrors.chapter_id && <p className={errorMessageClass}>{fieldErrors.chapter_id}</p>}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <label className={labelClass}>Business Name *</label>
                                <input type="text" value={formData.business_name} onChange={(e) => handleInputChange('business_name', e.target.value)} className={inputClass} />
                                {fieldErrors.business_name && <p className={errorMessageClass}>{fieldErrors.business_name}</p>}
                            </div>
                            <div>
                                <label className={labelClass}>Full Name (Owner) *</label>
                                <input type="text" value={formData.fullName} onChange={(e) => handleInputChange('fullName', e.target.value)} className={inputClass} />
                                {fieldErrors.fullName && <p className={errorMessageClass}>{fieldErrors.fullName}</p>}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <label className={labelClass}>Joining Date *</label>
                                <input type="date" value={formData.joiningDate} onChange={(e) => handleInputChange('joiningDate', e.target.value)} className={inputClass} />
                                {fieldErrors.joiningDate && <p className={errorMessageClass}>{fieldErrors.joiningDate}</p>}
                            </div>
                            <div className="relative">
                                <label className={labelClass}>Reference</label>
                                <input type="text" value={referenceSearch} onChange={handleReferenceInputChange} className={inputClass} placeholder="Search member..." />
                                {showReferenceDropdown && referenceResults.length > 0 && (
                                    <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                                        {referenceResults.map((member) => (
                                            <div key={member.user_id} onClick={() => handleReferenceSelect(member)} className="flex items-center p-3 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-b-0">
                                                <div className="font-medium text-slate-800 text-sm">{member.full_name}</div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <label className={labelClass}>Tagline</label>
                                <input type="text" value={formData.tagline} onChange={(e) => handleInputChange('tagline', e.target.value)} className={inputClass} />
                                {fieldErrors.tagline && <p className={errorMessageClass}>{fieldErrors.tagline}</p>}
                            </div>
                        </div>
                        <div>
                            <label className={labelClass}>Services (Press Enter or + to add)</label>
                            
                            {/* Display added services as tags */}
                            {formData.business_services.length > 0 && (
                                <div className="flex flex-wrap gap-2 mb-3 p-3 bg-slate-50 rounded-xl border border-slate-200 min-h-[50px]">
                                    {formData.business_services.map((service, index) => (
                                        <div key={index} className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium border border-blue-200">
                                            <span>{service}</span>
                                            <button 
                                                type="button"
                                                onClick={() => handleRemoveService(index)} 
                                                className="hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                                                title="Remove service"
                                            >
                                                <X className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            
                            {/* Input field for new service */}
                            <div className="flex gap-2">
                                <input 
                                    type="text" 
                                    value={serviceInput} 
                                    onChange={(e) => setServiceInput(e.target.value)} 
                                    onKeyDown={handleServiceKeyDown}
                                    placeholder="Type service name and press Enter"
                                    className={inputClass}
                                />
                                <button 
                                    type="button"
                                    onClick={handleAddService} 
                                    className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors flex items-center gap-2 font-bold"
                                    title="Add service"
                                >
                                    <Plus className="w-4 h-4" /> Add
                                </button>
                            </div>
                            {fieldErrors.business_services && <p className={errorMessageClass}>{fieldErrors.business_services}</p>}
                        </div>
                        <div><label className={labelClass}>Description</label><textarea value={formData.description} onChange={(e) => handleInputChange('description', e.target.value)} className={inputClass} rows="3" /></div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <label className={labelClass}>Personal Phone</label>
                                <input 
                                    type="tel" 
                                    value={formData.personal_phone_number} 
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                                        handleInputChange('personal_phone_number', value);
                                    }} 
                                    className={inputClass}
                                    placeholder="10 digits only"
                                    maxLength={10}
                                />
                                {fieldErrors.personal_phone_number && <p className={errorMessageClass}>{fieldErrors.personal_phone_number}</p>}
                            </div>
                            <div>
                                <label className={labelClass}>Office Phone</label>
                                <input 
                                    type="tel" 
                                    value={formData.office_phone_number} 
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                                        handleInputChange('office_phone_number', value);
                                    }} 
                                    className={inputClass}
                                    placeholder="10 digits only"
                                    maxLength={10}
                                />
                                {fieldErrors.office_phone_number && <p className={errorMessageClass}>{fieldErrors.office_phone_number}</p>}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <label className={labelClass}>Business Email</label>
                                <input type="email" value={formData.business_email} onChange={(e) => handleInputChange('business_email', e.target.value)} className={inputClass} />
                                {fieldErrors.business_email && <p className={errorMessageClass}>{fieldErrors.business_email}</p>}
                            </div>
                            <div>
                                <label className={labelClass}>Website</label>
                                <input type="url" value={formData.web_url} onChange={(e) => handleInputChange('web_url', e.target.value)} className={inputClass} placeholder="https://example.com" />
                                {fieldErrors.web_url && <p className={errorMessageClass}>{fieldErrors.web_url}</p>}
                            </div>
                        </div>
                    </div>
                );
            case 'address':
                return (
                      <div className="space-y-8 animate-fade-in-up">
                        <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                            <h5 className="font-bold text-slate-700 mb-3 text-sm flex items-center gap-2"><MapPin className="w-4 h-4" /> Hometown</h5>
                            <div className="space-y-4">
                                <div className="relative">
                                    <label className={labelClass}>Address</label>
                                    <input
                                        type="text"
                                        value={formData.hometown_address}
                                        onChange={(e) => handleAddressInputChange('hometown', e.target.value)}
                                        onFocus={() => handleAddressFocus('hometown')}
                                        onBlur={() => handleAddressBlur('hometown')}
                                        className={inputClass}
                                        placeholder="Start typing address"
                                    />
                                    {fieldErrors.hometown_address && <p className={errorMessageClass}>{fieldErrors.hometown_address}</p>}
                                    {showAddressSuggestions.hometown && addressSuggestions.hometown.length > 0 && (
                                        <ul className="absolute z-[1000] mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-56 overflow-y-auto">
                                            {addressSuggestions.hometown.map((prediction) => (
                                                <li
                                                    key={prediction.place_id}
                                                    onMouseDown={(e) => {
                                                        e.preventDefault();
                                                        handleAddressSuggestionSelect('hometown', prediction);
                                                    }}
                                                    className="px-3 py-2 cursor-pointer hover:bg-slate-100 text-sm text-slate-700"
                                                >
                                                    <div className="font-semibold text-slate-700">
                                                        {prediction.structured_formatting?.main_text || prediction.description}
                                                    </div>
                                                    {prediction.structured_formatting?.secondary_text && (
                                                        <div className="text-xs text-slate-500">
                                                            {prediction.structured_formatting.secondary_text}
                                                        </div>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                                <div className="grid grid-cols-3 gap-3">
                                    <div>
                                        <label className={labelClass}>City</label>
                                        <input type="text" value={formData.hometown_city} onChange={(e) => handleInputChange('hometown_city', e.target.value)} className={inputClass} />
                                        {fieldErrors.hometown_city && <p className={errorMessageClass}>{fieldErrors.hometown_city}</p>}
                                    </div>
                                    <div>
                                        <label className={labelClass}>District</label>
                                        <input type="text" value={formData.hometown_district} onChange={(e) => handleInputChange('hometown_district', e.target.value)} className={inputClass} />
                                        {fieldErrors.hometown_district && <p className={errorMessageClass}>{fieldErrors.hometown_district}</p>}
                                    </div>
                                    <div>
                                        <label className={labelClass}>Pincode</label>
                                        <input 
                                            type="text" 
                                            value={formData.hometown_pincode} 
                                            onChange={(e) => {
                                                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                                                handleInputChange('hometown_pincode', value);
                                            }} 
                                            className={inputClass}
                                            placeholder="6 digits"
                                            maxLength={6}
                                        />
                                        {fieldErrors.hometown_pincode && <p className={errorMessageClass}>{fieldErrors.hometown_pincode}</p>}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                            <h5 className="font-bold text-slate-700 mb-3 text-sm flex items-center gap-2"><MapPin className="w-4 h-4" /> Residence</h5>
                            <div className="space-y-4">
                                <div className="relative">
                                    <label className={labelClass}>Address</label>
                                    <input
                                        type="text"
                                        value={formData.residence_address}
                                        onChange={(e) => handleAddressInputChange('residence', e.target.value)}
                                        onFocus={() => handleAddressFocus('residence')}
                                        onBlur={() => handleAddressBlur('residence')}
                                        className={inputClass}
                                        placeholder="Start typing address"
                                    />
                                    {fieldErrors.residence_address && <p className={errorMessageClass}>{fieldErrors.residence_address}</p>}
                                    {showAddressSuggestions.residence && addressSuggestions.residence.length > 0 && (
                                        <ul className="absolute z-[1000] mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-56 overflow-y-auto">
                                            {addressSuggestions.residence.map((prediction) => (
                                                <li
                                                    key={prediction.place_id}
                                                    onMouseDown={(e) => {
                                                        e.preventDefault();
                                                        handleAddressSuggestionSelect('residence', prediction);
                                                    }}
                                                    className="px-3 py-2 cursor-pointer hover:bg-slate-100 text-sm text-slate-700"
                                                >
                                                    <div className="font-semibold text-slate-700">
                                                        {prediction.structured_formatting?.main_text || prediction.description}
                                                    </div>
                                                    {prediction.structured_formatting?.secondary_text && (
                                                        <div className="text-xs text-slate-500">
                                                            {prediction.structured_formatting.secondary_text}
                                                        </div>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                                <div className="grid grid-cols-3 gap-3">
                                    <div>
                                        <label className={labelClass}>City</label>
                                        <input type="text" value={formData.residence_city} onChange={(e) => handleInputChange('residence_city', e.target.value)} className={inputClass} />
                                        {fieldErrors.residence_city && <p className={errorMessageClass}>{fieldErrors.residence_city}</p>}
                                    </div>
                                    <div>
                                        <label className={labelClass}>District</label>
                                        <input type="text" value={formData.residence_district} onChange={(e) => handleInputChange('residence_district', e.target.value)} className={inputClass} />
                                        {fieldErrors.residence_district && <p className={errorMessageClass}>{fieldErrors.residence_district}</p>}
                                    </div>
                                    <div>
                                        <label className={labelClass}>Pincode</label>
                                        <input 
                                            type="text" 
                                            value={formData.residence_pincode} 
                                            onChange={(e) => {
                                                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                                                handleInputChange('residence_pincode', value);
                                            }} 
                                            className={inputClass}
                                            placeholder="6 digits"
                                            maxLength={6}
                                        />
                                        {fieldErrors.residence_pincode && <p className={errorMessageClass}>{fieldErrors.residence_pincode}</p>}
                                    </div>
                                </div>
                                <input type="hidden" value={formData.residence_latitude || ''} readOnly />
                                <input type="hidden" value={formData.residence_longitude || ''} readOnly />
                            </div>
                        </div>
                        <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                            <h5 className="font-bold text-slate-700 mb-3 text-sm flex items-center gap-2"><Building className="w-4 h-4" /> Office</h5>
                            <div className="space-y-4">
                                <div className="relative">
                                    <label className={labelClass}>Address</label>
                                    <input
                                        type="text"
                                        value={formData.office_address}
                                        onChange={(e) => handleAddressInputChange('office', e.target.value)}
                                        onFocus={() => handleAddressFocus('office')}
                                        onBlur={() => handleAddressBlur('office')}
                                        className={inputClass}
                                        placeholder="Start typing address"
                                    />
                                    {fieldErrors.office_address && <p className={errorMessageClass}>{fieldErrors.office_address}</p>}
                                    {showAddressSuggestions.office && addressSuggestions.office.length > 0 && (
                                        <ul className="absolute z-[1000] mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-56 overflow-y-auto">
                                            {addressSuggestions.office.map((prediction) => (
                                                <li
                                                    key={prediction.place_id}
                                                    onMouseDown={(e) => {
                                                        e.preventDefault();
                                                        handleAddressSuggestionSelect('office', prediction);
                                                    }}
                                                    className="px-3 py-2 cursor-pointer hover:bg-slate-100 text-sm text-slate-700"
                                                >
                                                    <div className="font-semibold text-slate-700">
                                                        {prediction.structured_formatting?.main_text || prediction.description}
                                                    </div>
                                                    {prediction.structured_formatting?.secondary_text && (
                                                        <div className="text-xs text-slate-500">
                                                            {prediction.structured_formatting.secondary_text}
                                                        </div>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                                <div className="grid grid-cols-3 gap-3">
                                    <div>
                                        <label className={labelClass}>City</label>
                                        <input type="text" value={formData.office_city} onChange={(e) => handleInputChange('office_city', e.target.value)} className={inputClass} />
                                        {fieldErrors.office_city && <p className={errorMessageClass}>{fieldErrors.office_city}</p>}
                                    </div>
                                    <div>
                                        <label className={labelClass}>District</label>
                                        <input type="text" value={formData.office_district} onChange={(e) => handleInputChange('office_district', e.target.value)} className={inputClass} />
                                        {fieldErrors.office_district && <p className={errorMessageClass}>{fieldErrors.office_district}</p>}
                                    </div>
                                    <div>
                                        <label className={labelClass}>Pincode</label>
                                        <input 
                                            type="text" 
                                            value={formData.office_pincode} 
                                            onChange={(e) => {
                                                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                                                handleInputChange('office_pincode', value);
                                            }} 
                                            className={inputClass}
                                            placeholder="6 digits"
                                            maxLength={6}
                                        />
                                        {fieldErrors.office_pincode && <p className={errorMessageClass}>{fieldErrors.office_pincode}</p>}
                                    </div>
                                </div>
                                <input type="hidden" value={formData.office_latitude || ''} readOnly />
                                <input type="hidden" value={formData.office_longitude || ''} readOnly />
                            </div>
                        </div>
                    </div>
                );
            case 'media':
                 return (
                    <div className="space-y-6 animate-fade-in-up">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div><label className={labelClass}>Profile Picture</label><input type="file" accept="image/*" onChange={(e) => handleInputChange('profile_picture', e.target.files[0])} className={inputClass} /></div>
                            <div><label className={labelClass}>Business Logo</label><input type="file" accept="image/*" onChange={(e) => handleInputChange('business_logo', e.target.files[0])} className={inputClass} /></div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div><label className={labelClass}>Business Card (Front)</label><input type="file" accept="image/*" onChange={(e) => handleInputChange('business_card_front', e.target.files[0])} className={inputClass} /></div>
                            <div><label className={labelClass}>Business Card (Back)</label><input type="file" accept="image/*" onChange={(e) => handleInputChange('business_card_back', e.target.files[0])} className={inputClass} /></div>
                        </div>
                        <div>
                            <label className={labelClass}>Portfolio Images</label>
                            <input type="file" accept="image/*" multiple onChange={(e) => handleInputChange('portfolio_images', Array.from(e.target.files))} className={inputClass} />
                            <p className="text-xs text-slate-400 mt-1">Select multiple images if needed.</p>
                        </div>
                    </div>
                );
            default: return null;
        }
    };

    return (
        <div className="space-y-5">
            <CustomStyles />

            {toastMessage && (
                <div className={`fixed bottom-6 right-6 px-6 py-3 rounded-xl shadow-2xl text-white font-bold flex items-center gap-3 z-[1000] animate-in slide-in-from-bottom-5 ${toastMessage.severity === 'success' ? 'bg-emerald-600' : toastMessage.severity === 'error' ? 'bg-red-500' : 'bg-blue-600'}`}>
                    {toastMessage.severity === 'success' ? <Check className="w-5 h-5" /> : <Info className="w-5 h-5" />}
                    {toastMessage.message}
                    <button onClick={() => setToastMessage(null)}><X className="w-4 h-4 ml-2" /></button>
                </div>
            )}
            
            {showDisableModal && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[1000] p-4 animate-in fade-in">
                      <div className="bg-white p-6 rounded-3xl shadow-2xl max-w-md w-full border border-slate-100 scale-100 animate-in zoom-in-95">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                                <Lock className="w-5 h-5 text-red-500"/> Disable Member
                            </h3>
                            <button onClick={() => setShowDisableModal(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5"/></button>
                        </div>
                        <p className="text-slate-500 mb-4 text-sm">Please provide a reason for disabling <strong>{userToDisable?.name}</strong>. This remark will be saved.</p>
                        
                        <div className="mb-6">
                            <label className={labelClass}>Remark / Reason *</label>
                            <textarea 
                                value={disableRemark} 
                                onChange={(e) => setDisableRemark(e.target.value)} 
                                className={`${inputClass} min-h-[100px] resize-none`}
                                placeholder="Enter reason here..."
                                autoFocus
                            />
                        </div>

                        <div className="flex gap-3">
                            <button onClick={() => setShowDisableModal(false)} className="flex-1 px-4 py-2.5 border border-slate-300 rounded-xl hover:bg-slate-50 text-slate-700 font-bold transition-colors">Cancel</button>
                            <button 
                                onClick={confirmDisableUser} 
                                disabled={isProcessingDisable}
                                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 font-bold transition-colors shadow-lg shadow-red-200 flex items-center justify-center gap-2"
                            >
                                {isProcessingDisable ? <RefreshCw className="w-4 h-4 animate-spin"/> : 'Confirm Disable'}
                            </button>
                        </div>
                      </div>
                </div>
            )}

            {confirmationDialog && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[999] p-4 animate-in fade-in">
                    <div className="bg-white p-6 rounded-3xl shadow-2xl max-w-sm w-full text-center border border-slate-100 scale-100 animate-in zoom-in-95">
                        <div className="w-14 h-14 bg-red-50 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-red-50">
                            <AlertTriangle className="w-6 h-6" />
                        </div>
                        <h3 className="text-lg font-bold text-slate-800 mb-2">{confirmationDialog.header}</h3>
                        <p className="text-slate-500 mb-6 font-medium">{confirmationDialog.message}</p>
                        <div className="flex gap-3">
                            <button onClick={() => { if (confirmationDialog.onCancel) confirmationDialog.onCancel(); setConfirmationDialog(null); }} className="flex-1 px-4 py-2.5 border border-slate-300 rounded-xl hover:bg-slate-50 text-slate-700 font-bold transition-colors">Cancel</button>
                            <button onClick={() => { if (confirmationDialog.onConfirm) confirmationDialog.onConfirm(); setConfirmationDialog(null); }} className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 font-bold transition-colors shadow-lg shadow-red-200">Confirm</button>
                        </div>
                    </div>
                </div>
            )}

             {/* --- View User Modal --- */}
            {pageMode === 'detail' && routeUserError && (
                <div className="flex items-start gap-3 rounded-2xl border border-red-100 bg-red-50 p-5 text-sm text-red-700">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <div>
                        <p className="font-semibold">{routeUserError}</p>
                        <button onClick={() => navigate('/admin/users')} className="mt-2 font-bold underline">
                            Back to members
                        </button>
                    </div>
                </div>
            )}

            {pageMode === 'detail' && viewUser && (
                <div className="space-y-5">
                    <button
                        onClick={() => navigate('/admin/users')}
                        className="flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-blue-600"
                    >
                        <ArrowLeft className="h-4 w-4" /> Back to members
                    </button>

                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                        <div className="flex flex-col bg-white">
                            {/* Header */}
                            <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-white z-10">
                                <div className="flex items-center gap-5">
                                    <div className="relative">
                                        {isValidImage(viewUser.profilePicture) ? (
                                            <img src={!viewUser.profilePicture.startsWith('http') ? `data:image/jpeg;base64,${viewUser.profilePicture}` : viewUser.profilePicture} alt="" className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-lg" />
                                        ) : (
                                            <div className="w-20 h-20 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-2xl font-bold border-4 border-white shadow-lg">
                                                {viewUser.first_name?.[0]}{viewUser.last_name?.[0]}
                                            </div>
                                        )}
                                        <div className={`absolute bottom-0 right-0 w-6 h-6 rounded-full border-2 border-white ${viewUser.isActive ? 'bg-emerald-500' : 'bg-red-500'}`}></div>
                                    </div>
                                    <div>
                                        <h2 className="text-2xl font-bold text-slate-800 mb-1">{viewUser.name}</h2>
                                        <div className="flex items-center gap-3 text-slate-500 text-sm font-medium">
                                            <span className="flex items-center gap-1 bg-slate-50 px-2 py-1 rounded-md border border-slate-100"><Briefcase className="w-3.5 h-3.5" /> {viewUser.businessName}</span>
                                            <span className="flex items-center gap-1 bg-slate-50 px-2 py-1 rounded-md border border-slate-100"><MapPin className="w-3.5 h-3.5" /> {viewUser.city}</span>
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => navigate(`/admin/users/${viewUser.id}/edit`)}
                                    className="flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-bold text-white shadow-sm shadow-blue-600/30 hover:bg-blue-700"
                                >
                                    <Pencil className="h-4 w-4" /> Edit member
                                </button>
                            </div>

                            {/* Tabs */}
                            <div className="flex gap-1 border-b border-slate-100 bg-white px-6">
                                {[
                                    { key: 'profile', label: 'Profile' },
                                    { key: 'activity', label: 'Activity record' },
                                ].map((tab) => (
                                    <button
                                        key={tab.key}
                                        onClick={() => setViewTab(tab.key)}
                                        className={`-mb-px border-b-2 px-4 py-3 text-sm font-bold transition-colors ${
                                            viewTab === tab.key
                                                ? 'border-blue-600 text-blue-700'
                                                : 'border-transparent text-slate-500 hover:text-slate-800'
                                        }`}
                                    >
                                        {tab.label}
                                    </button>
                                ))}
                            </div>

                            {/* Content */}
                            <div className="p-5 bg-slate-50/50">
                              {viewTab === 'activity' ? (
                                <MemberActivity
                                    userId={viewUser.id}
                                    chapterId={viewUser.chapterId}
                                    initialSection={linkedSection}
                                    initialDateRange={linkedDateRange}
                                    initialTenureId={linkedTenureId}
                                    initialStartDate={linkedStartDate}
                                    initialEndDate={linkedEndDate}
                                />
                              ) : (
                                <>
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                                    {/* Column 1 */}
                                    <div className="space-y-5">
                                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
                                            <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-5 pb-2 border-b border-slate-50 flex items-center gap-2">
                                                <User className="w-4 h-4 text-blue-500" /> Personal Details
                                            </h3>
                                            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                                                <InfoField label="Full Name" value={viewUser.name} />
                                                <InfoField label="DOB" value={viewUser.dob} />
                                                <InfoField label="Email" value={viewUser.email} />
                                                <InfoField label="Personal Phone" value={viewUser.personalPhoneNumber} />
                                                <InfoField label="Role" value={viewUser.role} />
                                                <InfoField
                                                    label="Status"
                                                    value={viewUser.accountStatus ? viewUser.accountStatus : (viewUser.isActive ? 'Active' : 'Inactive')}
                                                />
                                            </div>
                                        </div>

                                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
                                            <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-5 pb-2 border-b border-slate-50 flex items-center gap-2">
                                                <MapPin className="w-4 h-4 text-emerald-500" /> Addresses
                                            </h3>
                                            <div className="space-y-5">
                                                <div>
                                                    <div className="text-xs font-bold text-slate-400 uppercase mb-2">Office</div>
                                                    <p className="text-sm text-slate-600 font-medium leading-relaxed">{viewUser.officeAddress || '-'}</p>
                                                    <div className="mt-2 text-xs text-slate-500 font-mono">
                                                        {viewUser.officeCity}, {viewUser.officeDistrict} - {viewUser.officePincode}
                                                    </div>
                                                    {(viewUser.officeLatitude || viewUser.officeLongitude) && (
                                                        <div className="mt-1 text-xs text-slate-500 font-mono">
                                                            Lat: {viewUser.officeLatitude || '-'}, Lng: {viewUser.officeLongitude || '-'}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="w-full h-px bg-slate-50"></div>
                                                <div>
                                                    <div className="text-xs font-bold text-slate-400 uppercase mb-2">Residence</div>
                                                    <p className="text-sm text-slate-600 font-medium leading-relaxed">{viewUser.residenceAddress || '-'}</p>
                                                    <div className="mt-2 text-xs text-slate-500 font-mono">
                                                        {viewUser.residenceCity}, {viewUser.residenceDistrict} - {viewUser.residencePincode}
                                                    </div>
                                                    {(viewUser.residenceLatitude || viewUser.residenceLongitude) && (
                                                        <div className="mt-1 text-xs text-slate-500 font-mono">
                                                            Lat: {viewUser.residenceLatitude || '-'}, Lng: {viewUser.residenceLongitude || '-'}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="w-full h-px bg-slate-50"></div>
                                                <div>
                                                    <div className="text-xs font-bold text-slate-400 uppercase mb-2">Hometown</div>
                                                    <p className="text-sm text-slate-600 font-medium leading-relaxed">{viewUser.hometownAddress || '-'}</p>
                                                    <div className="mt-2 text-xs text-slate-500 font-mono">
                                                        {viewUser.hometownCity}, {viewUser.hometownDistrict} - {viewUser.hometownPincode}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Column 2 */}
                                    <div className="space-y-5">
                                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
                                            <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-5 pb-2 border-b border-slate-50 flex items-center gap-2">
                                                <Briefcase className="w-4 h-4 text-purple-500" /> Business Info
                                            </h3>
                                            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                                                <InfoField label="Business Name" value={viewUser.businessName} fullWidth />
                                                <InfoField label="Tagline" value={viewUser.businessTagline} fullWidth />
                                                <InfoField label="Chapter" value={viewUser.chapter} />
                                                <InfoField label="Joining Date" value={viewUser.joiningDate} />
                                                <InfoField label="Reference" value={viewUserReferenceName || viewUser.reference} />
                                                <InfoField label="Office Phone" value={viewUser.officePhoneNumber} />
                                                <InfoField label="Business Email" value={viewUser.email2} />
                                                <InfoField label="Website" value={viewUser.businessWebsite} />
                                                <InfoField label="Description" value={viewUser.descriptionOfBusiness} fullWidth />
                                                <InfoField label="Business Services" value={viewUser.businessServices} fullWidth />
                                            </div>
                                        </div>

                                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
                                            <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-5 pb-2 border-b border-slate-50 flex items-center gap-2">
                                                <ImageIcon className="w-4 h-4 text-amber-500" /> Media & Portfolio
                                            </h3>
                                            <div className="space-y-4">
                                                <div>
                                                    <label className="block text-xs font-bold text-slate-400 uppercase mb-2">Business Logo</label>
                                                    {isValidImage(viewUser.businessLogo) ? (
                                                        <img src={!viewUser.businessLogo.startsWith('http') ? `data:image/jpeg;base64,${viewUser.businessLogo}` : viewUser.businessLogo} alt="Logo" className="h-16 w-auto object-contain rounded-lg border border-slate-100" />
                                                    ) : <span className="text-sm text-slate-400 font-medium">-</span>}
                                                </div>
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-xs font-bold text-slate-400 uppercase mb-2">Card Front</label>
                                                        {isValidImage(viewUser.businessCardFront) ? (
                                                            <img src={!viewUser.businessCardFront.startsWith('http') ? `data:image/jpeg;base64,${viewUser.businessCardFront}` : viewUser.businessCardFront} alt="Front" className="w-full h-auto rounded-lg border border-slate-100 shadow-sm" />
                                                        ) : <div className="h-24 bg-slate-50 rounded-lg border border-slate-100 flex items-center justify-center text-slate-300 text-xs">No Image</div>}
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-bold text-slate-400 uppercase mb-2">Card Back</label>
                                                        {isValidImage(viewUser.businessCardBack) ? (
                                                            <img src={!viewUser.businessCardBack.startsWith('http') ? `data:image/jpeg;base64,${viewUser.businessCardBack}` : viewUser.businessCardBack} alt="Back" className="w-full h-auto rounded-lg border border-slate-100 shadow-sm" />
                                                        ) : <div className="h-24 bg-slate-50 rounded-lg border border-slate-100 flex items-center justify-center text-slate-300 text-xs">No Image</div>}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {viewUser.portfolioImages && viewUser.portfolioImages.length > 0 && (
                                    <div className="mt-8 bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
                                            <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-5 pb-2 border-b border-slate-50 flex items-center gap-2">
                                                <ImageIcon className="w-4 h-4 text-pink-500" /> Portfolio
                                            </h3>
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                                {viewUser.portfolioImages.map((img, idx) => (
                                                    <div key={idx} className="aspect-square rounded-xl overflow-hidden border border-slate-100">
                                                        <img src={!img.startsWith('http') ? `data:image/jpeg;base64,${img}` : img} alt={`Portfolio ${idx}`} className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" />
                                                    </div>
                                                ))}
                                            </div>
                                    </div>
                                )}
                                </>
                              )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {pageMode === 'list' && (
            <div className="space-y-5">
                {/* --- Filter Card --- */}
                {/* ADDED 'relative z-20' HERE TO FIX DROPDOWN ISSUE */}
                <div className="relative z-20 bg-white rounded-2xl shadow-sm border border-slate-200 p-5 animate-fade-in-up">
                    <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 mb-4 pb-3 border-b border-slate-100">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-50 rounded-xl text-blue-600"><UsersIcon className="w-5 h-5" /></div>
                            <h2 className="text-base font-bold text-slate-800 uppercase tracking-wide">Filter Members</h2>
                        </div>

                        {/* --- TAB STYLE STATISTICS (Interactive) --- */}
                        <div className="flex flex-wrap gap-2 p-1 bg-slate-100 rounded-xl">
                            <button 
                                onClick={() => setStatusTab('total')}
                                className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-all ${statusTab === 'total' ? 'bg-white shadow-sm text-blue-700' : 'text-slate-500 hover:bg-slate-200'}`}
                            >
                                <span className="text-xs font-bold uppercase tracking-wider">Total</span>
                                <span className={`text-sm font-extrabold ${statusTab === 'total' ? 'text-blue-700' : 'text-slate-700'}`}>{userStats.totalUsers}</span>
                            </button>
                            
                            <button 
                                onClick={() => setStatusTab('active')}
                                className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-all ${statusTab === 'active' ? 'bg-white shadow-sm text-emerald-700' : 'text-slate-500 hover:bg-slate-200'}`}
                            >
                                <span className="text-xs font-bold uppercase tracking-wider">Enabled</span>
                                <span className={`text-sm font-extrabold ${statusTab === 'active' ? 'text-emerald-700' : 'text-slate-700'}`}>{userStats.enabledUsers}</span>
                            </button>

                            <button 
                                onClick={() => setStatusTab('inactive')}
                                className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-all ${statusTab === 'inactive' ? 'bg-white shadow-sm text-red-600' : 'text-slate-500 hover:bg-slate-200'}`}
                            >
                                <span className="text-xs font-bold uppercase tracking-wider">Disabled</span>
                                <span className={`text-sm font-extrabold ${statusTab === 'inactive' ? 'text-red-600' : 'text-slate-700'}`}>{userStats.disabledUsers}</span>
                            </button>
                        </div>
                    </div>

                    {/* Filter Inputs Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-5 items-end">
                        <div className="relative">
                            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 ml-1">Search</label>
                            <div className="relative">
                                <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search users..." className="w-full p-2.5 pl-10 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-medium text-slate-700" />
                                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3.5" />
                            </div>
                        </div>

                        {/* --- Multi-Select Cities (Updated with Dashboard Dropdown) --- */}
                        <MultiSelectDropdown 
                            label="City"
                            placeholder="Select cities..."
                            options={allCities}
                            selectedValues={filterCities}
                            onSelectChange={setFilterCities}
                            disabled={Boolean(master.chapterId)}
                        />

                        {/* --- Multi-Select Chapters (Updated with Dashboard Dropdown) --- */}
                        <MultiSelectDropdown 
                            label="Chapters"
                            placeholder="Select chapters..."
                            options={allChapters.filter(ch => filterCities.length === 0 || filterCities.includes(ch.city_id?.name || ch.city))}
                            selectedValues={filterChapters}
                            onSelectChange={setFilterChapters}
                            disabled={Boolean(master.chapterId)}
                        />

                        <div className="flex gap-2 items-end h-full">
                             {/* --- Download PDF Button --- */}
                             <button
                                onClick={handleDownloadPdf}
                                disabled={downloadingPdf}
                                className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white px-4 py-2.5 rounded-xl text-sm font-bold shadow-lg shadow-emerald-200 hover:shadow-emerald-300 transition-all flex items-center justify-center gap-2 h-[46px]"
                                title="Download PDF"
                             >
                                {downloadingPdf ? <Loader className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
                                <span className="hidden sm:inline">{downloadingPdf ? 'Downloading...' : 'PDF'}</span>
                             </button>

                             {/* --- Add Member Button --- */}
                             <button onClick={() => navigate('/admin/users/new')} className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl text-sm font-bold shadow-lg shadow-blue-200 hover:shadow-blue-300 transition-all flex items-center justify-center gap-2 h-[46px] flex-1">
                                <Plus className="w-5 h-5" /> <span>Add</span>
                            </button>

                            <button onClick={() => { setSearchTerm(''); if (!master.chapterId) { setFilterCities([]); setFilterChapters([]); } setStatusTab('total'); }} className="px-3 py-2.5 bg-white border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50 hover:text-blue-600 hover:border-blue-200 transition-all shadow-sm h-[46px]" title="Reset Filters"><RefreshCw className="w-5 h-5" /></button>
                        </div>
                    </div>
                    <div className="mt-3"><MasterChapterLockNote /></div>
                </div>

                {/* --- Users Table Card --- */}
                <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
                    
                    {/* --- Rows per page selector --- */}
                    <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/30">
                        <h3 className="font-bold text-slate-700">All Members</h3>
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-slate-500 font-medium">Rows:</span>
                            <select 
                                value={itemsPerPage} 
                                onChange={(e) => setItemsPerPage(Number(e.target.value))} 
                                className="bg-white border border-slate-200 text-slate-700 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-1.5 outline-none font-bold cursor-pointer"
                            >
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                            </select>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-100">
                            <thead className="bg-slate-50/50">
                                <tr>
                                    {['Sr.', 'Name', 'Business', 'City', 'Chapter', 'Actions'].map(h => (
                                        <th key={h} className="px-5 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-slate-100">
                                {loading ? (
                                    <tr><td colSpan="6" className="px-6 py-10 text-center text-slate-400 font-medium">Loading users...</td></tr>
                                ) : users.length > 0 ? (
                                    users.map((user, index) => (
                                        <tr key={user.id} className="hover:bg-slate-50/80 transition-colors">
                                            <td className="px-5 py-3 whitespace-nowrap text-sm font-bold text-slate-500">{((currentPage - 1) * itemsPerPage) + index + 1}</td>
                                            <td className="px-5 py-3 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    {isValidImage(user.profilePicture) ? (
                                                        <img src={!user.profilePicture.startsWith('http') ? `data:image/jpeg;base64,${user.profilePicture}` : user.profilePicture} alt="" className="w-9 h-9 rounded-full object-cover border border-slate-200 shadow-sm mr-3" />
                                                    ) : (
                                                        <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold mr-3 border border-blue-200">
                                                            {user.first_name?.[0]}{user.last_name?.[0]}
                                                        </div>
                                                    )}
                                                    <div className="font-bold text-slate-800 text-sm">{user.name}</div>
                                                </div>
                                            </td>
                                            <td className="px-5 py-3 whitespace-nowrap text-sm font-medium text-slate-600">{user.businessName}</td>
                                            <td className="px-5 py-3 whitespace-nowrap text-sm font-medium text-slate-600"><span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-bold bg-slate-100 text-slate-600 border border-slate-200">{user.city}</span></td>
                                            <td className="px-5 py-3 whitespace-nowrap text-sm font-medium text-slate-600">{user.chapter}</td>
                                            <td className="px-5 py-3 whitespace-nowrap">
                                                <div className="flex gap-2">
                                                    <button onClick={() => { setViewTab('profile'); navigate(`/admin/users/${user.id}`); }} title="View member" className="p-2 rounded-lg text-blue-600 bg-blue-50 hover:bg-blue-100 transition-colors"><Eye className="w-4 h-4" /></button>
                                                    <button onClick={() => navigate(`/admin/users/${user.id}/edit`)} title="Edit member" className="p-2 rounded-lg text-amber-600 bg-amber-50 hover:bg-amber-100 transition-colors"><Pencil className="w-4 h-4" /></button>
                                                    <button onClick={() => handleDeleteUser(user.id)} className="p-2 rounded-lg text-red-600 bg-red-50 hover:bg-red-100 transition-colors"><Trash className="w-4 h-4" /></button>

                                                    <button 
                                                        onClick={() => handleToggleClick(user)} 
                                                        disabled={togglingUserId === user.id}
                                                        className={`p-2 rounded-lg transition-colors ${user.isActive ? 'text-emerald-600 bg-emerald-50 hover:bg-emerald-100' : 'text-red-600 bg-red-50 hover:bg-red-100'}`}
                                                        title={user.isActive ? "Click to Disable" : "Click to Enable"}
                                                    >
                                                        {togglingUserId === user.id ? (
                                                            <RefreshCw className="w-4 h-4 animate-spin" />
                                                        ) : user.isActive ? (
                                                            <LockOpen className="w-4 h-4" />
                                                        ) : (
                                                            <Lock className="w-4 h-4" />
                                                        )}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan="6" className="px-6 py-10 text-center text-slate-400 font-medium">No users found.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    {/* Pagination */}
                    <div className="px-6 py-4 bg-white border-t border-slate-100 flex items-center justify-between">
                        <div className="text-sm font-medium text-slate-500">
                            Showing <span className="font-bold text-slate-700">{users.length > 0 ? ((currentPage - 1) * itemsPerPage) + 1 : 0}</span> to <span className="font-bold text-slate-700">{Math.min(currentPage * itemsPerPage, totalRecords)}</span> of <span className="font-bold text-slate-700">{totalRecords}</span> entries
                        </div>
                        <div className="flex gap-2">
                            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-4 py-2 border border-slate-200 rounded-lg text-sm font-bold text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">Previous</button>
                            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages || totalPages === 0} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm">Next</button>
                        </div>
                    </div>
                </div>
            </div>
            )}

            {/* --- Add / Edit member (own page) --- */}
            {(pageMode === 'new' || pageMode === 'edit') && showAddEditModal && (
                <div className="space-y-5">
                    <button
                        onClick={handleCloseModal}
                        className="flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-blue-600"
                    >
                        <ArrowLeft className="h-4 w-4" /> Back to members
                    </button>

                    <div className="flex min-h-[70vh] overflow-hidden rounded-2xl border border-slate-200 bg-white">

                        {/* Section Navigation */}
                        <div className="hidden w-64 shrink-0 flex-col border-r border-slate-200 bg-slate-50 lg:flex">
                            <div className="p-6 border-b border-slate-200">
                                <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                                    <div className={`p-2 rounded-lg ${modalMode === 'add' ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'}`}>
                                        {modalMode === 'add' ? <Plus className="w-4 h-4" /> : <Pencil className="w-4 h-4" />}
                                    </div>
                                    {modalMode === 'add' ? 'Add New Member' : 'Edit Member'}
                                </h3>
                            </div>
                            <div className="flex-1 p-4 space-y-1">
                                <button onClick={() => setActiveTab('personal')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${activeTab === 'personal' ? 'bg-white text-blue-600 shadow-sm ring-1 ring-slate-200' : 'text-slate-500 hover:bg-slate-100'}`}>
                                    <User className="w-4 h-4" /> Personal Info
                                </button>
                                <button onClick={() => setActiveTab('business')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${activeTab === 'business' ? 'bg-white text-blue-600 shadow-sm ring-1 ring-slate-200' : 'text-slate-500 hover:bg-slate-100'}`}>
                                    <Briefcase className="w-4 h-4" /> Business & Contact
                                </button>
                                <button onClick={() => setActiveTab('address')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${activeTab === 'address' ? 'bg-white text-blue-600 shadow-sm ring-1 ring-slate-200' : 'text-slate-500 hover:bg-slate-100'}`}>
                                    <MapPin className="w-4 h-4" /> Addresses
                                </button>
                                <button onClick={() => setActiveTab('media')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${activeTab === 'media' ? 'bg-white text-blue-600 shadow-sm ring-1 ring-slate-200' : 'text-slate-500 hover:bg-slate-100'}`}>
                                    <ImageIcon className="w-4 h-4" /> Images & Media
                                </button>
                            </div>
                            <div className="p-4 border-t border-slate-200">
                                <button onClick={handleCloseModal} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-slate-300 text-slate-600 font-bold hover:bg-white transition-colors">
                                    <X className="w-4 h-4" /> Cancel
                                </button>
                            </div>
                        </div>

                        {/* Main Content */}
                        <div className="flex min-w-0 flex-1 flex-col bg-white">
                            {/* Section switcher for narrow screens, where the rail is hidden */}
                            <div className="flex gap-1 overflow-x-auto border-b border-slate-100 px-5 lg:hidden">
                                {[
                                    { key: 'personal', label: 'Personal' },
                                    { key: 'business', label: 'Business' },
                                    { key: 'address', label: 'Addresses' },
                                    { key: 'media', label: 'Media' },
                                ].map((t) => (
                                    <button
                                        key={t.key}
                                        onClick={() => setActiveTab(t.key)}
                                        className={`-mb-px whitespace-nowrap border-b-2 px-3 py-3 text-sm font-bold ${
                                            activeTab === t.key
                                                ? 'border-blue-600 text-blue-700'
                                                : 'border-transparent text-slate-500'
                                        }`}
                                    >
                                        {t.label}
                                    </button>
                                ))}
                            </div>
                            <div className="flex-1 p-8">
                                <h2 className="text-xl font-bold text-slate-800 mb-6 pb-2 border-b border-slate-100">
                                    {activeTab === 'personal' && 'Personal Information'}
                                    {activeTab === 'business' && 'Business & Contact Information'}
                                    {activeTab === 'address' && 'Address Information'}
                                    {activeTab === 'media' && 'Images & Media'}
                                </h2>
                                {renderAddEditContent()}
                            </div>
                            <div className="p-6 border-t border-slate-100 bg-slate-50 flex justify-end gap-3">
                                <button onClick={handleCloseModal} className="px-6 py-2.5 rounded-xl font-bold text-slate-600 hover:bg-slate-200 transition-colors">Discard</button>
                                <button onClick={handleSubmit} disabled={modalLoading} className="px-8 py-2.5 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 shadow-lg shadow-blue-200 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                                    {modalLoading ? <Loader className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                                    {modalMode === 'add' ? 'Create Member' : 'Update Member'}
                                </button>
                            </div>
                        </div>

                    </div>
                </div>
            )}

        </div>
    );
};

export default Users;